import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faUpload, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import './EditCampaignModal.css';

/**
 * EditCampaignModal - Modal for editing campaign fields
 *
 * Supported fields by platform:
 *
 * Facebook:
 * - Status (ACTIVE/PAUSED) - campaign, ad set, and creative level
 * - Daily Budget (ad set level only, in dollars)
 * - Bid Strategy (ad set level only): LOWEST_COST_WITHOUT_CAP, LOWEST_COST_WITH_BID_CAP, COST_CAP
 * - Bid Amount (ad set level, in dollars, required for LOWEST_COST_WITH_BID_CAP and COST_CAP)
 * - Campaign Spend Limit (campaign level, in dollars) - cap total campaign spend
 * - End Date Update (campaign and ad set level) - update campaign/ad set end date
 * - Creative Landing Page (creative level) - update landing page URL for creatives
 * - Geography Targeting (ad set level) - add cities/locations to ad set targeting
 * - Interest Targeting (ad set level) - add interest keywords to ad set targeting
 *
 * Reddit:
 * - Status (ACTIVE/PAUSED) - campaign and ad group level
 * - Daily Budget (ad group level only, in dollars)
 * - Bid Value (ad group level only, in dollars)
 * - Campaign Spend Limit (campaign level, in dollars)
 * - End Date Update (campaign level) - update campaign end date
 * - Geography Targeting (ad group level) - add zipcodes to targeting
 * - Community Targeting (ad group level) - add subreddits to targeting
 * - Keyword Targeting (ad group level) - add keywords to targeting
 *
 * Google Ads Search:
 * - Status (ENABLED/PAUSED) - campaign and ad group level
 * - Daily Budget (campaign level, in dollars)
 * - CPC Bid (ad group level, in dollars)
 * - Campaign Spend Limit (campaign level) - total spend cap
 * - Location Targeting (campaign level) - add locations to campaign
 * - Keyword Targeting (ad group level) - add keywords to ad group
 *
 * MadHive:
 * - Status (ACTIVE/PAUSED) - campaign and line item level
 * - Update Campaign Name - campaign level
 * - Update Flight Dates - campaign level (start/end dates on all line items)
 * - Update Geo Targeting - campaign level (postal codes, replace/add/remove, all line items)
 * - Update Daily Frequency Cap - campaign level (1-20, all line items)
 * - Update Dayparting Exclusions - campaign level (days + hour range when ads will NOT run)
 * - Update Impression Goal - campaign level (all line items)
 * - Delete Campaign - campaign level (permanently archives the campaign)
 *
 * YouTube DV360:
 * - Status (ACTIVE/PAUSED) - campaign level only
 *
 * DV360:
 * - Prompt only. One free-text instruction is planned across the campaign by
 *   campaign-services/campaign_editor/dv360_group_edit.py, which picks the entities the
 *   prompt names ("all PTG lines", "the display IO") and applies the change to each. The
 *   operation/level/entity pickers other platforms use do not apply — the prompt says
 *   both what to change and what to change it on.
 * - Both DV360 levels are editable, and the prompt decides which one it means:
 *     line items       bids, geo, audiences, frequency caps, creative settings
 *     insertion orders flight dates, total budget or impression goal, pacing
 *   Flight dates and money live on the insertion order in DV360, so "extend the campaign
 *   through December" and "raise the budget to 60k" are IO edits even though they read
 *   like campaign-level ones.
 */
const EditCampaignModal = ({ isOpen, onClose, campaign, onSubmit, isSubmitting, orchestrationStatus, campaignMessages, platformCampaignId, allPlatformCampaignIds = [], accountId, userId }) => {
  const [operation, setOperation] = useState('status');
  const [level, setLevel] = useState('campaign');
  const [statusValue, setStatusValue] = useState('');
  const [budgetValue, setBudgetValue] = useState('');
  const [bidStrategy, setBidStrategy] = useState('');
  const [bidAmount, setBidAmount] = useState('');
  const [description, setDescription] = useState('');
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  // New state variables for Facebook operations
  const [spendLimitValue, setSpendLimitValue] = useState('');
  const [endDateValue, setEndDateValue] = useState('');
  const [landingPageValue, setLandingPageValue] = useState('');
  const [geographyValue, setGeographyValue] = useState('');
  const [interestValue, setInterestValue] = useState('');
  // Reddit-specific state variables
  const [communityValue, setCommunityValue] = useState('');
  const [keywordValue, setKeywordValue] = useState('');
  // Google Ads-specific state variables
  const [locationValue, setLocationValue] = useState('');
  // TTD-specific state variables
  const [adgroupIdValue, setAdgroupIdValue] = useState('');
  const [audienceIdValue, setAudienceIdValue] = useState('');
  const [audienceNameValue, setAudienceNameValue] = useState('');
  const [selectedTtdCampaignId, setSelectedTtdCampaignId] = useState('');
  const [selectedTtdCampaignIds, setSelectedTtdCampaignIds] = useState([]);
  const [selectedAdgroupIds, setSelectedAdgroupIds] = useState([]);
  // MadHive-specific state variables
  const [madhiveLineItemId, setMadhiveLineItemId] = useState('');
  const [madhiveAdvertiserId, setMadhiveAdvertiserId] = useState('');
  const [publisherGroupName, setPublisherGroupName] = useState('');
  const [publisherGroupId, setPublisherGroupId] = useState('');
  const [madhiveMediaType, setMadhiveMediaType] = useState('DISPLAY');
  const [madhiveProductId, setMadhiveProductId] = useState('');
  const [publishersCsvFile, setPublishersCsvFile] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [madhivePixelName, setMadhivePixelName] = useState('');
  const [madhiveSegmentName, setMadhiveSegmentName] = useState('');
  const [madhiveRetargetingWindow, setMadhiveRetargetingWindow] = useState('30 DAY');
  const [madhiveCampaignName, setMadhiveCampaignName] = useState('');
  const [madhiveStartDate, setMadhiveStartDate] = useState('');
  const [madhiveEndDate, setMadhiveEndDate] = useState('');
  const [madhivePostalCodes, setMadhivePostalCodes] = useState('');
  const [madhiveGeoMode, setMadhiveGeoMode] = useState('replace');
  const [madhiveFcapDaily, setMadhiveFcapDaily] = useState('');
  const [madhiveImpressionGoal, setMadhiveImpressionGoal] = useState('');
  const [madhiveOptTemplateDomains, setMadhiveOptTemplateDomains] = useState('');
  const [madhiveDaypartDays, setMadhiveDaypartDays] = useState([]);
  const [madhiveDaypartStartHour, setMadhiveDaypartStartHour] = useState('0');
  const [madhiveDaypartEndHour, setMadhiveDaypartEndHour] = useState('23');
  const [madhiveDaypartMode, setMadhiveDaypartMode] = useState('replace');
  // PMP deals state
  const [pmpDeals, setPmpDeals] = useState([]);
  const [selectedDealIds, setSelectedDealIds] = useState([]);
  const [pmpDealsLoading, setPmpDealsLoading] = useState(false);
  // General file upload + text area for JSON config
  const [configTextArea, setConfigTextArea] = useState('');
  const [configDragActive, setConfigDragActive] = useState(false);
  const configFileInputRef = useRef(null);
  // Auto-load launcher config state
  const [configLoading, setConfigLoading] = useState(false);
  const [configAutoLoaded, setConfigAutoLoaded] = useState(false);
  const [launchConfigGcsPath, setLaunchConfigGcsPath] = useState('');

  const platform = campaign?.destination?.toLowerCase();

  // DV360 is edited by prompt alone: no operation list, no level, no entity picker.
  // The backend group editor resolves all three from the prompt text.
  const isPromptOnly = platform === 'dv360';

  // TTD operations that support multi-select for campaigns and ad groups
  const ttdMultiSelectOps = ['extend_end_date', 'update_campaign_budget', 'update_conversion_reporting', 'assign_audience', 'assign_deals_to_adgroup', 'refresh_audience'];
  const isTtdMultiSelect = platform === 'ttd' && ttdMultiSelectOps.includes(operation);

  // Get platform config early so we can use it in reset
  const getPlatformConfigForReset = () => {
    if (platform === 'ttd') return { firstOp: 'extend_end_date' };
    if (platform === 'dv360') return { firstOp: 'group_edit' };
    return { firstOp: 'status' };
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      const resetConfig = getPlatformConfigForReset();
      setOperation(resetConfig.firstOp);
      setLevel('campaign');
      setStatusValue('');
      setBudgetValue('');
      setBidStrategy('');
      setBidAmount('');
      setDescription('');
      setSelectedEntities([]);
      setSelectAll(true);
      setSelectedCampaignId('');
      // Reset new Facebook fields
      setSpendLimitValue('');
      setEndDateValue('');
      setLandingPageValue('');
      setGeographyValue('');
      setInterestValue('');
      // Reset Reddit fields
      setCommunityValue('');
      setKeywordValue('');
      // Reset Google Ads fields
      setLocationValue('');
      // Reset TTD fields
      setAdgroupIdValue('');
      setAudienceIdValue('');
      setAudienceNameValue('');
      // Default to the clicked campaign ID, or first available
      setSelectedTtdCampaignId(platformCampaignId || (allPlatformCampaignIds.length > 0 ? allPlatformCampaignIds[0].id : ''));
      // Multi-select: default to clicked campaign, or all campaigns
      setSelectedTtdCampaignIds(
        platformCampaignId ? [platformCampaignId] : allPlatformCampaignIds.map(c => c.id)
      );
      setSelectedAdgroupIds([]);
      // Reset MadHive campaign edit fields
      setMadhiveCampaignName('');
      setMadhiveStartDate('');
      setMadhiveEndDate('');
      setMadhivePostalCodes('');
      setMadhiveGeoMode('replace');
      setMadhiveFcapDaily('');
      setMadhiveImpressionGoal('');
      setMadhiveDaypartDays([]);
      setMadhiveDaypartStartHour('0');
      setMadhiveDaypartEndHour('23');
      setMadhiveDaypartMode('replace');
      // Reset PMP deals
      setSelectedDealIds([]);
      // Reset config file/text area
      setConfigTextArea('');
      setConfigDragActive(false);
      // Reset auto-load state
      setConfigLoading(false);
      setConfigAutoLoaded(false);
      setLaunchConfigGcsPath('');
    }
  }, [isOpen]);

  // Helper: find the launcher_result GCS path.
  // Priority:
  //   1. orchestrationStatus.launcherResultPath (from DB, always uncorrupted)
  //   2. fast-track completion message json_response (written by fastTrackOrchestrator
  //      directly from DB, uncorrupted — identified by workflowType field)
  //   3. Any message with launcher_result (LLM-extracted, may have truncated UUIDs)
  const getLauncherResultPath = () => {
    if (orchestrationStatus?.launcherResultPath) {
      return orchestrationStatus.launcherResultPath;
    }
    if (!campaignMessages || campaignMessages.length === 0) return null;
    // Fast-track completion messages (uncorrupted, written directly from DB)
    for (let i = campaignMessages.length - 1; i >= 0; i--) {
      const jr = campaignMessages[i].json_response;
      if (jr?.launcher_result && (jr.workflowType === 'fast-track-shell' || jr.workflowType === 'fast-track')) {
        return jr.launcher_result;
      }
    }
    return null;
  };

  // Account billing currency for the money input labels/placeholders. The
  // optimizer backend resolves it from the Google Ads customer.currency_code
  // and stores it on the analysis result; scan the campaign's messages for the
  // most recent one. Falls back to USD when no optimizer result is present
  // (preserves the legacy label for platforms that don't surface currency).
  const getAccountCurrency = () => {
    if (!campaignMessages || campaignMessages.length === 0) return 'USD';
    for (let i = campaignMessages.length - 1; i >= 0; i--) {
      const code = campaignMessages[i]?.json_response?.analysis_data?.campaign_structure?.currency_code;
      if (code) return code;
    }
    return 'USD';
  };
  const accountCurrency = getAccountCurrency();

  // Auto-fetch launcher config when create_audience is selected for MadHive
  useEffect(() => {
    if (operation !== 'create_audience' || platform !== 'madhive') return;
    if (configAutoLoaded || configTextArea.trim() !== '') return;

    const launcherPath = getLauncherResultPath();
    if (!launcherPath) return;

    const gcsPath = launcherPath.endsWith('.json')
      ? launcherPath
      : (launcherPath.endsWith('/')
          ? `${launcherPath}parsed_response.json`
          : `${launcherPath}/parsed_response.json`);

    setLaunchConfigGcsPath(gcsPath);

    let cancelled = false;
    setConfigLoading(true);

    api.get(getApiUrl(`/api/v2/campaigns/${campaign.id}/gcs-json`), {
      params: { gcsPath, accountId, userId }
    })
      .then(response => {
        if (cancelled) return;
        const content = response.data?.content;
        if (content) {
          setConfigTextArea(JSON.stringify(content, null, 2));
          setConfigAutoLoaded(true);
        }
      })
      .catch(() => {
        // Silent fail — user can still see status
      })
      .finally(() => {
        if (!cancelled) setConfigLoading(false);
      });

    return () => { cancelled = true; };
  }, [operation, platform, configAutoLoaded, configTextArea, campaign?.id, campaignMessages]);

  // Auto-fetch PMP deals when assign_deals_to_adgroup is selected for TTD
  useEffect(() => {
    if (operation !== 'assign_deals_to_adgroup' || platform !== 'ttd') return;
    if (pmpDeals.length > 0) return;

    let cancelled = false;
    setPmpDealsLoading(true);

    api.get(getApiUrl(`/api/v2/campaigns/pmp-deals`), {
      params: { accountId, userId }
    })
      .then(response => {
        if (cancelled) return;
        const deals = response.data?.deals;
        if (deals && Array.isArray(deals)) {
          setPmpDeals(deals);
        }
      })
      .catch((err) => {
        console.error('Failed to load PMP deals:', err);
      })
      .finally(() => {
        if (!cancelled) setPmpDealsLoading(false);
      });

    return () => { cancelled = true; };
  }, [operation, platform]);

  // Auto-fetch launcher config when refresh_audience is selected for TTD
  useEffect(() => {
    if (operation !== 'refresh_audience' || platform !== 'ttd') return;
    if (configAutoLoaded || configTextArea.trim() !== '') return;

    const launcherPath = getLauncherResultPath();
    if (!launcherPath) return;

    // launcher_result is the direct GCS path (may already point to parsed_response.json or a folder)
    const gcsPath = launcherPath.endsWith('.json')
      ? launcherPath
      : (launcherPath.endsWith('/')
          ? `${launcherPath}parsed_response.json`
          : `${launcherPath}/parsed_response.json`);

    setLaunchConfigGcsPath(gcsPath);

    let cancelled = false;
    setConfigLoading(true);

    api.get(getApiUrl(`/api/v2/campaigns/${campaign.id}/gcs-json`), {
      params: { gcsPath, accountId, userId }
    })
      .then(response => {
        if (cancelled) return;
        const content = response.data?.content;
        if (content) {
          setConfigTextArea(JSON.stringify(content, null, 2));
          setConfigAutoLoaded(true);
        }
      })
      .catch(() => {
        // Silent fail — user can still manually upload
      })
      .finally(() => {
        if (!cancelled) setConfigLoading(false);
      });

    return () => { cancelled = true; };
  }, [operation, platform, configAutoLoaded, configTextArea, campaign?.id, campaignMessages]);

  // Get platform-specific configuration
  const getPlatformConfig = () => {
    if (platform === 'facebook' || platform === 'fb') {
      return {
        name: 'Facebook',
        operations: [
          { value: 'status', label: 'Status Update' },
          { value: 'daily_budget', label: 'Daily Budget Update' },
          { value: 'bid_strategy', label: 'Bid Strategy Update' },
          { value: 'campaign_spend_limit', label: 'Campaign Spend Limit' },
          { value: 'extend_end_date', label: 'End Date Update' },
          { value: 'creative_landing_page', label: 'Creative Landing Page' },
          { value: 'geography_targeting', label: 'Geography Targeting' },
          { value: 'interest_targeting', label: 'Interest Targeting' }
        ],
        levels: {
          status: [
            { value: 'campaign', label: 'Campaign' },
            { value: 'ad_set', label: 'Ad Set' },
            { value: 'creative', label: 'Creative' }
          ],
          daily_budget: [
            { value: 'campaign', label: 'Campaign' }
          ],
          bid_strategy: [
            { value: 'campaign', label: 'Campaign' }
          ],
          campaign_spend_limit: [
            { value: 'campaign', label: 'Campaign' }
          ],
          extend_end_date: [
            { value: 'campaign', label: 'Campaign (updates all ad sets)' },
            { value: 'ad_set', label: 'Ad Set' }
          ],
          creative_landing_page: [
            { value: 'creative', label: 'Creative' }
          ],
          geography_targeting: [
            { value: 'ad_set', label: 'Ad Set' }
          ],
          interest_targeting: [
            { value: 'ad_set', label: 'Ad Set' }
          ]
        },
        statusValues: ['ACTIVE', 'PAUSED'],
        bidStrategies: [
          { value: 'LOWEST_COST_WITHOUT_BID_CAP', label: 'Lowest Cost (No Bid Cap)' },
          { value: 'LOWEST_COST_WITH_BID_CAP', label: 'Lowest Cost With Bid Cap' },
          { value: 'COST_CAP', label: 'Cost Cap' }
        ]
      };
    } else if (platform === 'reddit') {
      return {
        name: 'Reddit',
        operations: [
          { value: 'status', label: 'Status Update' },
          { value: 'daily_budget', label: 'Daily Budget Update' },
          { value: 'bid_strategy', label: 'Bid Value Update' },
          { value: 'campaign_spend_limit', label: 'Campaign Spend Limit' },
          { value: 'extend_end_date', label: 'End Date Update' },
          { value: 'geography_targeting', label: 'Geography Targeting' },
          { value: 'community_targeting', label: 'Community Targeting' },
          { value: 'keyword_targeting', label: 'Keyword Targeting' }
        ],
        levels: {
          status: [
            { value: 'campaign', label: 'Campaign' },
            { value: 'ad_group', label: 'Ad Group' }
          ],
          daily_budget: [
            { value: 'ad_group', label: 'Ad Group' }
          ],
          bid_strategy: [
            { value: 'ad_group', label: 'Ad Group' }
          ],
          campaign_spend_limit: [
            { value: 'campaign', label: 'Campaign' }
          ],
          extend_end_date: [
            { value: 'campaign', label: 'Campaign (updates all ad groups)' }
          ],
          geography_targeting: [
            { value: 'ad_group', label: 'Ad Group' }
          ],
          community_targeting: [
            { value: 'ad_group', label: 'Ad Group' }
          ],
          keyword_targeting: [
            { value: 'ad_group', label: 'Ad Group' }
          ]
        },
        statusValues: ['ACTIVE', 'PAUSED']
      };
    } else if (platform === 'google-ads-search' || platform === 'google_search') {
      return {
        name: 'Google Ads Search',
        operations: [
          { value: 'status', label: 'Status Update' },
          { value: 'budget', label: 'Budget Update' },
          { value: 'campaign_spend_limit', label: 'Campaign Spend Limit' },
          { value: 'location_targeting', label: 'Location Targeting' },
          { value: 'keyword_targeting', label: 'Add Keywords' },
          { value: 'pause_keywords', label: 'Pause Keywords' }
        ],
        levels: {
          status: [
            { value: 'campaign', label: 'Campaign' },
            { value: 'ad_group', label: 'Ad Group' }
          ],
          budget: [
            { value: 'campaign', label: 'Campaign (Daily Budget)' },
            { value: 'ad_group', label: 'Ad Group (CPC Bid)' }
          ],
          campaign_spend_limit: [
            { value: 'campaign', label: 'Campaign' }
          ],
          location_targeting: [
            { value: 'campaign', label: 'Campaign' }
          ],
          keyword_targeting: [
            { value: 'ad_group', label: 'Ad Group' }
          ],
          pause_keywords: [
            { value: 'ad_group', label: 'Ad Group' }
          ]
        },
        statusValues: ['ENABLED', 'PAUSED']
      };
    } else if (platform === 'madhive') {
      return {
        name: 'MadHive',
        operations: [
          { value: 'status', label: 'Status Update' },
          { value: 'update_campaign_name', label: 'Update Campaign Name' },
          { value: 'update_campaign_dates', label: 'Update Flight Dates' },
          { value: 'update_campaign_geo', label: 'Update Geo Targeting (Postal Codes)' },
          { value: 'update_campaign_fcap', label: 'Update Daily Frequency Cap' },
          { value: 'update_campaign_daypart', label: 'Update Dayparting Exclusions' },
          { value: 'update_campaign_budget', label: 'Update Impression Goal' },
          { value: 'zero_domains_from_campaign_opt_template', label: 'Zero Out Domains in Optimization Template' },
          { value: 'create_retargeting_audience', label: 'Create Retargeting Audience' },
          { value: 'create_website_retargeting_audience', label: 'Create Website Retargeting Audience' },
          { value: 'apply_audience', label: 'Apply Audience to Line Item' },
          { value: 'create_publisher_group', label: 'Create Publisher Group' },
          { value: 'assign_publisher_group', label: 'Assign Publisher Group to Line Item' },
          { value: 'create_audience', label: 'Create Audience' },
          { value: 'delete', label: 'Delete Campaign' }
        ],
        levels: {
          status: [
            { value: 'campaign', label: 'Campaign' },
            { value: 'line_item', label: 'Line Item' },
            { value: 'creative', label: 'Creative' }
          ],
          update_campaign_name: [
            { value: 'campaign', label: 'Campaign' }
          ],
          update_campaign_dates: [
            { value: 'campaign', label: 'Campaign' }
          ],
          update_campaign_geo: [
            { value: 'campaign', label: 'Campaign' }
          ],
          update_campaign_fcap: [
            { value: 'campaign', label: 'Campaign' }
          ],
          update_campaign_daypart: [
            { value: 'campaign', label: 'Campaign' }
          ],
          update_campaign_budget: [
            { value: 'campaign', label: 'Campaign' }
          ],
          zero_domains_from_campaign_opt_template: [
            { value: 'campaign', label: 'Campaign' }
          ],
          delete: [
            { value: 'campaign', label: 'Campaign' }
          ],
          create_retargeting_audience: [
            { value: 'campaign', label: 'Campaign' }
          ],
          create_website_retargeting_audience: [
            { value: 'campaign', label: 'Campaign' }
          ],
          apply_audience: [
            { value: 'line_item', label: 'Line Item' }
          ],
          create_publisher_group: [
            { value: 'campaign', label: 'Campaign' }
          ],
          assign_publisher_group: [
            { value: 'line_item', label: 'Line Item' }
          ],
          create_audience: [
            { value: 'campaign', label: 'Campaign' }
          ]
        },
        statusValues: ['ACTIVE', 'PAUSED']
      };
    } else if (platform === 'youtube-dv360' || platform === 'youtube_dv360' || platform === 'dv360-youtube') {
      return {
        name: 'YouTube DV360',
        operations: [
          { value: 'status', label: 'Status Update' }
        ],
        levels: {
          status: [
            { value: 'campaign', label: 'Campaign' }
          ]
        },
        statusValues: ['ACTIVE', 'PAUSED']
      };
    } else if (platform === 'dv360') {
      return {
        name: 'DV360',
        operations: [
          { value: 'group_edit', label: 'Prompt' }
        ],
        levels: {
          group_edit: [
            { value: 'campaign', label: 'Campaign' }
          ]
        },
        statusValues: []
      };
    } else if (platform === 'ttd') {
      return {
        name: 'The Trade Desk',
        operations: [
          { value: 'extend_end_date', label: 'Extend End Date' },
          { value: 'update_campaign_budget', label: 'Update Campaign Budget' },
          { value: 'update_conversion_reporting', label: 'Update Conversion Reporting' },
          { value: 'update_adgroup_name', label: 'Update Ad Group Name' },
          { value: 'assign_audience', label: 'Assign Audience' },
          { value: 'create_adgroup', label: 'Create Ad Group' },
          { value: 'refresh_audience', label: 'Refresh Audience' },
          { value: 'assign_deals_to_adgroup', label: 'Assign PMP Deals' }
        ],
        levels: {
          extend_end_date: [
            { value: 'campaign', label: 'Campaign' }
          ],
          update_campaign_budget: [
            { value: 'campaign', label: 'Campaign' }
          ],
          update_conversion_reporting: [
            { value: 'campaign', label: 'Campaign' }
          ],
          update_adgroup_name: [
            { value: 'ad_group', label: 'Ad Group' }
          ],
          assign_audience: [
            { value: 'ad_group', label: 'Ad Group' }
          ],
          create_adgroup: [
            { value: 'campaign', label: 'Campaign' }
          ],
          refresh_audience: [
            { value: 'campaign', label: 'Campaign' }
          ],
          assign_deals_to_adgroup: [
            { value: 'ad_group', label: 'Ad Group' }
          ]
        },
        statusValues: []
      };
    }
    return null;
  };

  const config = getPlatformConfig();
  const currentLevels = config?.levels?.[operation] || [];

  // Auto-select first available level when operation changes
  useEffect(() => {
    if (currentLevels.length > 0 && !currentLevels.find(l => l.value === level)) {
      setLevel(currentLevels[0].value);
    }
  }, [operation, currentLevels, level]);

  // Get available ad groups/ad sets from orchestration result for the specific campaign
  const getAvailableEntities = () => {
    // Need platformCampaignId to filter entities
    if (!platformCampaignId) return [];

    // For import-campaign, we may not have orchestrationStatus but can get data from campaignMessages
    const apiResult = orchestrationStatus?.apiResult || null;
    const details = apiResult?.details || null;

    console.log('🔍 getAvailableEntities - platformCampaignId:', platformCampaignId);
    console.log('🔍 getAvailableEntities - platform:', platform);
    console.log('🔍 getAvailableEntities - apiResult:', apiResult);
    console.log('🔍 getAvailableEntities - campaignMessages:', campaignMessages?.length);

    if (platform === 'reddit') {
      // For Reddit: filter ad groups by campaign_id
      const adgroups = details?.adgroups || apiResult?.adgroups || [];
      console.log('🔍 Reddit all adgroups:', adgroups);

      // Filter ad groups that belong to this specific campaign
      const filteredAdgroups = adgroups.filter(ag => ag.campaign_id === platformCampaignId);
      console.log('🔍 Reddit filtered adgroups for campaign', platformCampaignId, ':', filteredAdgroups);

      return filteredAdgroups.map(ag => ({
        id: ag.id,
        name: ag.name || `Ad Group ${ag.id}`,
        type: 'adgroup',
        campaign_id: ag.campaign_id
      }));
    } else if (platform === 'facebook' || platform === 'fb') {
      // For Facebook creative level: get creatives/ads from refresh-campaign, import-campaign, or assign-creatives messages
      if (level === 'creative') {
        console.log('🔍 Facebook looking for creatives, campaignMessages:', campaignMessages);

        const allCreatives = [];
        const seenCreativeIds = new Set();

        // PRIORITY 1: Check for the latest successful refresh-campaign message
        const refreshMessages = campaignMessages?.filter(msg => {
          const isRefreshAction = msg.action === 'refresh-campaign';
          const isCompleted = msg.status === 'completed';
          const jsonResp = msg.json_response;
          const hasSuccess = jsonResp?.success === true;
          // Check if campaign_id matches
          const campaignId = jsonResp?.details?.campaign?.id;
          const matchesCampaign = !campaignId || String(campaignId) === String(platformCampaignId);
          return isRefreshAction && isCompleted && hasSuccess && matchesCampaign;
        }) || [];

        console.log('🔍 Facebook found refresh-campaign messages:', refreshMessages.length);

        if (refreshMessages.length > 0) {
          // Use the most recent refresh-campaign message
          const latestRefreshMessage = refreshMessages[refreshMessages.length - 1];
          const refreshResult = latestRefreshMessage.json_response;
          const adsets = refreshResult?.details?.campaign?.adsets || [];

          console.log('🔍 Facebook using refresh-campaign data, adsets:', adsets.length);

          adsets.forEach(adSet => {
            const ads = adSet.ads || [];
            ads.forEach(ad => {
              const adId = ad.id || ad.ad_id;
              if (adId && !seenCreativeIds.has(adId)) {
                seenCreativeIds.add(adId);
                allCreatives.push({
                  id: adId,
                  name: ad.name || `Ad ${adId}`,
                  type: 'creative',
                  campaign_id: platformCampaignId,
                  ad_set_id: adSet.id || ad.adset_id,
                  landing_page: ad.landing_page_url || ad.creative?.object_story_spec?.link_data?.link ||
                               ad.creative?.object_story_spec?.video_data?.call_to_action?.value?.link || ''
                });
              }
            });
          });

          console.log('🔍 Facebook creatives from refresh-campaign:', allCreatives.length, allCreatives);
          return allCreatives;
        }

        // PRIORITY 2: Check import-campaign messages for ads
        const importMessages = campaignMessages?.filter(msg => {
          const isImportAction = msg.action === 'import-campaign';
          const isCompleted = msg.status === 'completed';
          const jsonResp = msg.json_response;
          const hasSuccess = jsonResp?.api_result?.success === true ||
                            jsonResp?.success === true ||
                            jsonResp?.status === 'completed';
          const msgPlatform = jsonResp?.platform || jsonResp?.api_result?.platform || '';
          const isFacebookPlatform = msgPlatform.includes('facebook') || msgPlatform === 'fb';
          return isImportAction && isCompleted && hasSuccess && isFacebookPlatform;
        }) || [];

        if (importMessages.length > 0) {
          const latestImportMessage = importMessages[importMessages.length - 1];
          const importResult = latestImportMessage.json_response?.api_result || latestImportMessage.json_response;
          const campaigns = importResult?.campaigns || [];

          let importedCampaign = campaigns.find(c => String(c.campaign_id) === String(platformCampaignId));
          if (!importedCampaign && campaigns.length === 1) {
            importedCampaign = campaigns[0];
          }

          if (importedCampaign && importedCampaign.ad_sets) {
            importedCampaign.ad_sets.forEach(adSet => {
              const ads = adSet.ads || [];
              ads.forEach(ad => {
                const adId = ad.id || ad.ad_id;
                if (adId && !seenCreativeIds.has(adId)) {
                  seenCreativeIds.add(adId);
                  allCreatives.push({
                    id: adId,
                    name: ad.name || `Ad ${adId}`,
                    type: 'creative',
                    campaign_id: platformCampaignId,
                    ad_set_id: adSet.id || adSet.ad_set_id,
                    landing_page: ad.landing_page_url || ad.creative?.object_story_spec?.link_data?.link || ''
                  });
                }
              });
            });
          }
        }

        // PRIORITY 3: Also check assign-creatives messages for created ads
        const assignMessages = campaignMessages?.filter(m =>
          (m.action === 'assign-creatives' || m.action === 'upload-creatives') &&
          m.json_response?.success === true
        ) || [];

        console.log('🔍 Facebook found assign-creatives messages:', assignMessages.length);

        assignMessages.forEach(message => {
          const response = message.json_response;
          // created_ads is nested inside details
          const createdAds = response.details?.created_ads || response.created_ads || [];

          console.log('🔍 Facebook processing assign-creatives message:', {
            messageId: message.message_id,
            createdAdsCount: createdAds.length,
            createdAds: createdAds
          });

          createdAds.forEach(ad => {
            const adId = ad.ad_id || ad.id;
            if (adId && !seenCreativeIds.has(adId)) {
              seenCreativeIds.add(adId);
              allCreatives.push({
                id: adId,
                name: ad.ad_name || ad.name || `Ad ${adId}`,
                type: 'creative',
                campaign_id: platformCampaignId,
                ad_set_id: ad.adset_id || ad.ad_set_id,
                adset_name: ad.adset_name
              });
            }
          });
        });

        console.log('🔍 Facebook total creatives collected:', allCreatives.length, allCreatives);
        return allCreatives;
      }

      // For Facebook ad set level: PRIORITY 1 - check refresh-campaign, then orchestration/import-campaign

      // PRIORITY 1: Check for the latest successful refresh-campaign message
      const refreshMessagesAdSet = campaignMessages?.filter(msg => {
        const isRefreshAction = msg.action === 'refresh-campaign';
        const isCompleted = msg.status === 'completed';
        const jsonResp = msg.json_response;
        const hasSuccess = jsonResp?.success === true;
        // Check if campaign_id matches
        const campaignId = jsonResp?.details?.campaign?.id;
        const matchesCampaign = !campaignId || String(campaignId) === String(platformCampaignId);
        return isRefreshAction && isCompleted && hasSuccess && matchesCampaign;
      }) || [];

      console.log('🔍 Facebook ad set level - found refresh-campaign messages:', refreshMessagesAdSet.length);

      if (refreshMessagesAdSet.length > 0) {
        // Use the most recent refresh-campaign message
        const latestRefreshMessage = refreshMessagesAdSet[refreshMessagesAdSet.length - 1];
        const refreshResult = latestRefreshMessage.json_response;
        const adsets = refreshResult?.details?.campaign?.adsets || [];

        console.log('🔍 Facebook using refresh-campaign data for ad sets, count:', adsets.length);

        return adsets.map(as => ({
          id: as.id,
          name: as.name || `Ad Set ${as.id}`,
          type: 'adset',
          campaign_id: platformCampaignId
        }));
      }

      // PRIORITY 2: Get ad sets from orchestration result
      let adSets = details?.ad_sets || apiResult?.ad_sets ||
                   apiResult?.successful_ad_sets || details?.successful_ad_sets || [];
      console.log('🔍 Facebook ad sets from orchestration:', adSets);

      // PRIORITY 3: If no ad sets found, check for import-campaign messages
      if (adSets.length === 0 && campaignMessages) {
        console.log('🔍 Facebook checking campaignMessages for import-campaign');

        const importMessages = campaignMessages.filter(msg => {
          const isImportAction = msg.action === 'import-campaign';
          const isCompleted = msg.status === 'completed';
          // Check for success in api_result OR at root level of json_response
          const jsonResp = msg.json_response;
          const hasSuccess = jsonResp?.api_result?.success === true ||
                            jsonResp?.success === true ||
                            jsonResp?.status === 'completed';
          // Check platform matches Facebook
          const msgPlatform = jsonResp?.platform || jsonResp?.api_result?.platform || '';
          const isFacebookPlatform = msgPlatform.includes('facebook') || msgPlatform === 'fb';
          return isImportAction && isCompleted && hasSuccess && isFacebookPlatform;
        });

        console.log('🔍 Facebook found import-campaign messages:', importMessages.length);

        if (importMessages.length > 0) {
          // Get the most recent import-campaign message
          const latestImportMessage = importMessages[importMessages.length - 1];
          const importResult = latestImportMessage.json_response?.api_result || latestImportMessage.json_response;

          // Find the campaign matching platformCampaignId (compare as strings)
          const campaigns = importResult?.campaigns || [];
          console.log('🔍 Facebook looking for campaign_id:', platformCampaignId, 'in campaigns:', campaigns.map(c => c.campaign_id));

          let importedCampaign = campaigns.find(c =>
            String(c.campaign_id) === String(platformCampaignId)
          );

          // If not found by campaign_id and there's only one campaign, use it
          if (!importedCampaign && campaigns.length === 1) {
            importedCampaign = campaigns[0];
            console.log('🔍 Facebook using single campaign from import:', importedCampaign);
          }

          console.log('🔍 Facebook found importedCampaign:', importedCampaign);

          if (importedCampaign && importedCampaign.ad_sets) {
            adSets = importedCampaign.ad_sets;
            console.log('🔍 Facebook ad sets from import-campaign:', adSets);
          }
        }
      }

      // Check if the campaign_id matches (if campaign_id field exists)
      // Otherwise, assume all ad sets belong to this campaign
      const filteredAdSets = adSets.filter(as => {
        // If ad set has campaign_id, filter by it
        if (as.campaign_id) {
          return as.campaign_id === platformCampaignId;
        }
        // For import-campaign ad sets, they already belong to the correct campaign
        // Otherwise, check if the apiResult.campaign_id matches
        return true;
      });

      console.log('🔍 Facebook filtered ad sets for campaign', platformCampaignId, ':', filteredAdSets);

      return filteredAdSets.map(as => ({
        id: as.ad_set_id || as.id,
        name: as.ad_set_name || as.name || `Ad Set ${as.ad_set_id || as.id}`,
        type: 'adset',
        campaign_id: platformCampaignId
      }));
    } else if (platform === 'google-ads-search' || platform === 'google_search') {
      // For Google Ads: find the specific campaign and get its ad groups
      let campaigns = (details?.campaigns?.length > 0 ? details.campaigns : null) ||
                      (apiResult?.campaigns?.length > 0 ? apiResult.campaigns : null) ||
                      [];
      console.log('🔍 Google Ads all campaigns from orchestration:', campaigns);

      // If no campaigns found in orchestration, check for import-campaign messages
      if (campaigns.length === 0 && campaignMessages) {
        console.log('🔍 Google Ads checking campaignMessages for import-campaign, total messages:', campaignMessages.length);

        const importMessages = campaignMessages.filter(msg => {
          const isImportAction = msg.action === 'import-campaign';
          const isCompleted = msg.status === 'completed';
          // Check for success in api_result OR at root level of json_response OR status=completed
          const jsonResp = msg.json_response;
          const hasSuccess = jsonResp?.api_result?.success === true ||
                            jsonResp?.success === true ||
                            jsonResp?.status === 'completed';
          // Check platform matches (google_search or google-ads-search)
          const msgPlatform = jsonResp?.platform || jsonResp?.api_result?.platform || '';
          const isGooglePlatform = msgPlatform.includes('google') || msgPlatform === 'google_search';
          return isImportAction && isCompleted && hasSuccess && isGooglePlatform;
        });

        console.log('🔍 Google Ads found import-campaign messages:', importMessages.length);

        if (importMessages.length > 0) {
          // Get the most recent import-campaign message
          const latestImportMessage = importMessages[importMessages.length - 1];
          const importResult = latestImportMessage.json_response?.api_result || latestImportMessage.json_response;
          campaigns = importResult?.campaigns || [];
          console.log('🔍 Google Ads campaigns from import-campaign:', campaigns);
        }
      }

      // Find target campaign by campaign_id - also check imported_campaign_id at root level
      let targetCampaign = campaigns.find(c => String(c.campaign_id) === String(platformCampaignId));

      // If not found by campaign_id in campaigns array, check if there's only one campaign
      if (!targetCampaign && campaigns.length === 1) {
        targetCampaign = campaigns[0];
        console.log('🔍 Google Ads using single campaign from import:', targetCampaign);
      }
      console.log('🔍 Google Ads target campaign for platformCampaignId', platformCampaignId, ':', targetCampaign);

      if (targetCampaign) {
        let adGroups = targetCampaign.ad_groups || [];
        console.log('🔍 Google Ads ad groups from campaign:', adGroups);

        // If no ad groups found in campaign, check assignment_results (for fast-track-shell workflows)
        if (adGroups.length === 0) {
          // First try to get from apiResult
          let assignmentResults = details?.assignment_results || apiResult?.assignment_results || [];
          console.log('🔍 Google Ads checking assignment_results from apiResult:', assignmentResults);

          // If not found in apiResult, check campaignMessages for assign-creatives messages
          if (assignmentResults.length === 0 && campaignMessages) {
            console.log('🔍 Google Ads checking campaignMessages for assign-creatives:', campaignMessages);

            // Find assign-creatives or upload-creatives messages
            const creativeMessages = campaignMessages.filter(msg =>
              (msg.action === 'assign-creatives' || msg.action === 'upload-creatives') &&
              msg.status === 'completed' &&
              msg.json_response
            );

            console.log('🔍 Google Ads found creative messages:', creativeMessages);

            // Get assignment_results from the most recent creative message
            if (creativeMessages.length > 0) {
              const latestCreativeMessage = creativeMessages[creativeMessages.length - 1];
              const creativeResult = latestCreativeMessage.json_response;
              assignmentResults = creativeResult?.details?.assignment_results ||
                                 creativeResult?.assignment_results || [];
              console.log('🔍 Google Ads assignment_results from creative message:', assignmentResults);
            }
          }

          // Extract ad groups from assignment results
          adGroups = assignmentResults
            .filter(result => result.success && result.assignment?.campaign_id === platformCampaignId)
            .map(result => ({
              id: result.ad_group_id,
              name: result.assignment?.adgroup_name || `Ad Group ${result.ad_group_id}`,
              ad_group_resource_name: result.ad_resource_name ?
                result.ad_resource_name.replace(/\/adGroupAds\/.*/, `/adGroups/${result.ad_group_id}`) :
                null
            }));

          console.log('🔍 Google Ads ad groups from assignment_results:', adGroups);
        }

        const mappedAdGroups = adGroups.map(ag => {
          // Extract ad group ID from resource name
          // Format: customers/1298625544/adGroups/190792226874
          let adGroupId = ag.id;
          if (!adGroupId && ag.ad_group_resource_name) {
            const parts = ag.ad_group_resource_name.split('/');
            adGroupId = parts[parts.length - 1];
          }

          return {
            id: adGroupId,
            name: ag.name || `Ad Group ${adGroupId}`,
            type: 'adgroup',
            campaign_id: platformCampaignId,
            resource_name: ag.ad_group_resource_name
          };
        });
        console.log('🔍 Google Ads FINAL mapped ad groups:', mappedAdGroups);
        return mappedAdGroups;
      }

      // No target campaign found
      console.log('🔍 Google Ads - no target campaign found, returning empty array');
      return [];
    } else if (platform === 'madhive') {
      // For MadHive: get line items or creatives based on level
      console.log('🔍 MadHive platformCampaignId:', platformCampaignId);
      console.log('🔍 MadHive level:', level);
      console.log('🔍 MadHive apiResult:', apiResult);
      console.log('🔍 MadHive details:', details);

      // If level is 'creative', get creatives from ALL assign-creatives or upload-creatives messages
      if (level === 'creative') {
        console.log('🔍 MadHive looking for creatives, campaignMessages:', campaignMessages);

        // Find ALL assign-creatives or upload-creatives messages (not just the first one)
        const assignCreativesMessages = campaignMessages?.filter(m =>
          (m.action === 'assign-creatives' || m.action === 'upload-creatives') &&
          m.json_response?.success === true  // Only include successful messages
        ) || [];

        console.log('🔍 MadHive found assign-creatives messages:', assignCreativesMessages.length);

        // Collect creatives from ALL messages
        const allCreatives = [];
        const seenCreativeIds = new Set(); // Track duplicates

        for (const message of assignCreativesMessages) {
          const response = message.json_response;
          const createdCreatives = response.created_creatives;
          const assignments = response.assignments;

          console.log('🔍 MadHive processing message:', {
            messageId: message.message_id,
            creativeCount: createdCreatives?.length || 0
          });

          if (createdCreatives && createdCreatives.length > 0) {
            // Map creatives from created_creatives array
            createdCreatives.forEach((creative) => {
              const creativeId = creative.id || creative.creativeId;

              // Skip duplicates (in case same creative appears in multiple messages)
              if (seenCreativeIds.has(creativeId)) {
                console.log('⚠️ Skipping duplicate creative:', creativeId);
                return;
              }
              seenCreativeIds.add(creativeId);

              // Try to find matching assignment to get line item info
              const assignment = assignments?.find(a => a.response?.creativeId === creativeId);
              const lineItemId = assignment?.response?.lineItemId;

              allCreatives.push({
                id: creativeId,
                name: `Creative ${creativeId}`,
                type: 'creative',
                campaign_id: platformCampaignId,
                line_item_id: lineItemId
              });
            });
          }
        }

        console.log('🔍 MadHive total creatives collected:', allCreatives.length);

        if (allCreatives.length === 0) {
          console.log('⚠️ No creatives found in MadHive campaign messages');
        }

        return allCreatives;
      }

      // For line_item level: get line items from processed_config
      const processedConfig = details?.processed_config || apiResult?.processed_config;
      console.log('🔍 MadHive processed_config:', processedConfig);

      if (processedConfig) {
        const lineItems = processedConfig.lineItems || [];
        console.log('🔍 MadHive lineItems from processed_config:', lineItems);

        // Also get line_item_ids from details for mapping
        const lineItemIds = details?.line_item_ids || apiResult?.line_item_ids || [];
        console.log('🔍 MadHive line_item_ids:', lineItemIds);

        // Map line items with their IDs
        return lineItems.map((li, index) => {
          const lineItemId = lineItemIds[index] || li.id || `line_item_${index}`;
          return {
            id: lineItemId,
            name: li.name || `Line Item ${lineItemId}`,
            type: 'line_item',
            campaign_id: platformCampaignId,
            // Store full line item data for reference
            data: li
          };
        });
      }

      // Fallback: try to get from campaigns array (old format)
      const campaigns = details?.campaigns || apiResult?.campaigns || [];
      console.log('🔍 MadHive all campaigns (fallback):', campaigns);

      const targetCampaign = campaigns.find(c => c.campaign_id === platformCampaignId);
      console.log('🔍 MadHive target campaign (fallback):', targetCampaign);

      if (targetCampaign && targetCampaign.line_items) {
        return targetCampaign.line_items.map(li => ({
          id: li.line_item_id || li.id,
          name: li.line_item_name || li.name || `Line Item ${li.line_item_id || li.id}`,
          type: 'line_item',
          campaign_id: platformCampaignId
        }));
      }
    } else if (platform === 'youtube-dv360' || platform === 'youtube_dv360' || platform === 'dv360-youtube' || platform === 'dv360') {
      // For DV360: try multiple data formats

      // Format 1: DV360 orchestration result with line_item_results[] at top level
      // Each item has response.lineItemId and response.displayName
      const lineItemResults = details?.line_item_results || apiResult?.line_item_results || [];
      console.log('🔍 DV360 line_item_results:', lineItemResults.length);

      if (lineItemResults.length > 0) {
        return lineItemResults
          .filter(li => li.success && li.response)
          .map(li => ({
            id: li.response.lineItemId || li.response.line_item_id,
            name: li.response.displayName || `Line Item ${li.response.lineItemId}`,
            type: 'line_item',
            campaign_id: li.response.campaignId || platformCampaignId
          }));
      }

      // Format 2: YouTube DV360 format with campaigns[].line_items[]
      const campaigns = details?.campaigns || apiResult?.campaigns || [];
      console.log('🔍 DV360 campaigns (fallback):', campaigns);

      const targetCampaign = campaigns.find(c => c.campaign_id === platformCampaignId);
      if (targetCampaign && targetCampaign.line_items) {
        return targetCampaign.line_items.map(li => ({
          id: li.line_item_id || li.id,
          name: li.line_item_name || li.name || `Line Item ${li.line_item_id || li.id}`,
          type: 'line_item',
          campaign_id: platformCampaignId
        }));
      }

      // Format 3: import-campaign message result with line_item_results[] built by the importer
      const importMessages = (campaignMessages || []).filter(msg =>
        msg.action === 'import-campaign' || msg.message_type === 'import-campaign'
      );
      if (importMessages.length > 0) {
        const latestImport = importMessages[importMessages.length - 1];
        const importResult = latestImport.json_response?.api_result || latestImport.json_response;
        const importLineItemResults = importResult?.line_item_results || [];
        if (importLineItemResults.length > 0) {
          return importLineItemResults
            .filter(li => li.success && li.response)
            .map(li => ({
              id: li.response.lineItemId || li.response.line_item_id,
              name: li.response.displayName || `Line Item ${li.response.lineItemId}`,
              type: 'line_item',
              campaign_id: li.response.campaignId || platformCampaignId
            }));
        }
      }
    } else if (platform === 'ttd') {
      // For TTD: extract ad group IDs from the orchestration result or import message
      // Primary source: adgroup_id_to_name (maps real TTD ad group IDs → names, from backend)
      // Secondary source: adgroup_id_to_campaign_id (may have real IDs after backend change)
      const importMsgs = (campaignMessages || []).filter(msg =>
        msg.action === 'import-campaign' || msg.message_type === 'import-campaign'
      );
      const latestImportMsg = importMsgs.length > 0 ? importMsgs[importMsgs.length - 1] : null;
      const importResult = latestImportMsg ? (latestImportMsg.json_response?.api_result || latestImportMsg.json_response) : null;
      const campaignResults = apiResult?.campaign_results || details?.campaign_results || importResult?.campaign_results || {};
      const adgroupToCampaignMap = campaignResults.adgroup_id_to_campaign_id || {};
      const adgroupNameMap = details?.adgroup_id_to_name || apiResult?.adgroup_id_to_name || importResult?.adgroup_id_to_name || {};
      // For multi-select operations, filter by all selected campaign IDs; otherwise use single selection
      const targetCampaignIds = isTtdMultiSelect && selectedTtdCampaignIds.length > 0
        ? selectedTtdCampaignIds
        : [selectedTtdCampaignId || platformCampaignId].filter(Boolean);

      // Build from adgroup_id_to_name (real IDs) + cross-reference with campaign mapping
      let adgroups = [];
      if (Object.keys(adgroupNameMap).length > 0) {
        // Use adgroup_id_to_name as primary — these are real TTD ad group IDs
        adgroups = Object.entries(adgroupNameMap)
          .filter(([adgroupId]) => {
            const campId = adgroupToCampaignMap[adgroupId];
            return targetCampaignIds.length === 0 || targetCampaignIds.includes(campId);
          })
          .map(([adgroupId, name]) => ({
            id: adgroupId,
            name: name || adgroupId,
            type: 'adgroup',
            campaign_id: adgroupToCampaignMap[adgroupId] || targetCampaignIds[0]
          }));
      } else if (Object.keys(adgroupToCampaignMap).length > 0) {
        // Fallback: use adgroup_id_to_campaign_id (may have config-style keys like "adGroup_1")
        adgroups = Object.entries(adgroupToCampaignMap)
          .filter(([_, campId]) => targetCampaignIds.includes(campId))
          .map(([adgroupId, campId]) => ({
            id: adgroupId,
            name: adgroupId,
            type: 'adgroup',
            campaign_id: campId
          }));
      }

      // Fallback: parse adgroups from import-campaign message campaigns dict
      // (format: { campaigns: { [campaignId]: { campaign_data: { adgroups: [...] } } } })
      if (adgroups.length === 0) {
        const importMessages = (campaignMessages || []).filter(msg =>
          msg.action === 'import-campaign' || msg.message_type === 'import-campaign'
        );
        if (importMessages.length > 0) {
          const latestImport = importMessages[importMessages.length - 1];
          const importResult = latestImport.json_response?.api_result || latestImport.json_response;
          const campaignsDict = importResult?.campaigns;
          if (campaignsDict && typeof campaignsDict === 'object' && !Array.isArray(campaignsDict)) {
            Object.entries(campaignsDict).forEach(([cid, cdata]) => {
              if (targetCampaignIds.length === 0 || targetCampaignIds.includes(cid)) {
                const rawAdgroups = cdata?.campaign_data?.adgroups || [];
                rawAdgroups.forEach(ag => {
                  adgroups.push({
                    id: ag.AdGroupId,
                    name: ag.AdGroupName || ag.AdGroupId,
                    type: 'adgroup',
                    campaign_id: ag.CampaignId || cid
                  });
                });
              }
            });
          }
        }
      }

      console.log('🔍 TTD ad groups for campaigns', targetCampaignIds, ':', adgroups);
      return adgroups;
    }

    return [];
  };

  const availableEntities = getAvailableEntities();
  const showEntitySelection = level !== 'campaign' && availableEntities.length > 0 && platform !== 'ttd' && platform !== 'dv360';

  // Auto-select all entities when level changes to ad group/ad set
  useEffect(() => {
    if (showEntitySelection && selectAll) {
      setSelectedEntities(availableEntities.map(e => e.id));
    } else if (!showEntitySelection) {
      setSelectedEntities([]);
    }
  }, [level, showEntitySelection, selectAll]);

  // Auto-select all ad groups for TTD assign_audience when entities change
  useEffect(() => {
    if (platform === 'ttd' && operation === 'assign_audience' && availableEntities.length > 0) {
      setSelectedAdgroupIds(availableEntities.map(e => e.id));
    }
  }, [platform, operation, availableEntities.length]);

  // Early return if modal is closed
  if (!isOpen) return null;

  if (!config) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Edit Campaign</h2>
            <button className="close-button" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div className="modal-body">
            <p>Campaign editing is not supported for platform: {platform}</p>
          </div>
        </div>
      </div>
    );
  }

  // Helper: returns true when file upload + text area should be shown
  const needsConfigInput = (op, plat) => {
    if (plat === 'ttd') {
      return ['update_campaign_budget', 'update_conversion_reporting', 'create_audience', 'create_adgroup'].includes(op);
    }
    return false;
  };

  // File upload handlers for JSON config
  const handleConfigDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setConfigDragActive(true);
    } else if (e.type === 'dragleave') {
      setConfigDragActive(false);
    }
  };

  const handleConfigDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setConfigDragActive(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    const jsonFile = droppedFiles.find(f => f.name.endsWith('.json'));
    if (jsonFile) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setConfigTextArea(event.target.result);
      };
      reader.readAsText(jsonFile);
    }
  };

  const handleConfigFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith('.json')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setConfigTextArea(event.target.result);
      };
      reader.readAsText(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Deleting a campaign is destructive and cannot be undone — gate it behind
    // an explicit confirmation before anything is sent to the backend.
    if (operation === 'delete') {
      const confirmed = window.confirm(
        'Delete this campaign? This permanently removes it on MadHive and cannot be undone.'
      );
      if (!confirmed) return;
    }

    console.log('🔍 EditCampaignModal handleSubmit - budgetValue:', budgetValue, 'operation:', operation, 'level:', level);

    // Prompt-only platforms send the user's text verbatim. Prefixing it with a
    // synthesized "Change campaign status to ..." sentence would give the group
    // editor's planner a second, contradictory instruction to reconcile.
    if (isPromptOnly) {
      onSubmit(description.trim(), [], {});
      return;
    }

    // Build description based on operation
    let desc = '';
    if (operation === 'status') {
      if (level === 'creative' && (platform === 'facebook' || platform === 'fb')) {
        // For Facebook creative status, format as expected by the backend
        desc = `${statusValue === 'PAUSED' ? 'pause' : 'activate'} creative`;
      } else {
        desc = `Change ${level} status to ${statusValue}`;
      }
    } else if (operation === 'delete') {
      // Prompt-driven on the backend — madhive_edit_campaign_reasoning.py
      // parses "Delete this campaign" and routes to the delete operation.
      desc = 'Delete this campaign';
    } else if (operation === 'daily_budget') {
      desc = `Set ${level} daily budget to $${budgetValue}`;
    } else if (operation === 'bid_strategy' && platform === 'reddit') {
      desc = `Set ${level} bid value to $${bidAmount}`;
    } else if (operation === 'bid_strategy' && (platform === 'facebook' || platform === 'fb')) {
      if (bidStrategy === 'LOWEST_COST_WITHOUT_BID_CAP') {
        desc = `change bid strategy for campaign to ${bidStrategy}`;
      } else {
        desc = `change bid strategy for campaign to ${bidStrategy} of $${bidAmount}`;
      }
    } else if (operation === 'budget') {
      if (level === 'campaign') {
        desc = `Set campaign daily budget to $${budgetValue}`;
      } else {
        desc = `Set ad group CPC bid to $${budgetValue}`;
      }
    } else if (operation === 'campaign_spend_limit') {
      desc = `cap total spend at $${spendLimitValue}`;
    } else if (operation === 'extend_end_date') {
      // Format date to human-readable format (e.g., "December 31, 2025")
      const dateObj = new Date(endDateValue + 'T00:00:00');
      const formattedDate = dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      desc = `extend ${level === 'campaign' ? 'campaign' : 'ad set'} to ${formattedDate}`;
    } else if (operation === 'creative_landing_page') {
      desc = `change landing page to ${landingPageValue}`;
    } else if (operation === 'geography_targeting') {
      if (platform === 'reddit') {
        desc = `add zipcode ${geographyValue} to additional geography targeting`;
      } else {
        desc = `Add ${geographyValue} as new geographies to these ad sets`;
      }
    } else if (operation === 'interest_targeting') {
      desc = `Add ${interestValue} as interests to all these adsets`;
    } else if (operation === 'community_targeting') {
      desc = `add ${communityValue} to community targeting`;
    } else if (operation === 'keyword_targeting') {
      if (platform === 'google-ads-search' || platform === 'google_search') {
        desc = `add keywords "${keywordValue}"`;
      } else {
        desc = `add ${keywordValue} to keywords targeting`;
      }
    } else if (operation === 'pause_keywords') {
      desc = `pause keywords "${keywordValue}"`;
    } else if (operation === 'location_targeting') {
      desc = `add ${locationValue} as location targeting to campaign`;
    }

    // MadHive advanced operations
    let configPayload = null;
    if (platform === 'madhive' && operation !== 'status') {
      const platformCampaignId = campaign?.platform_campaign_id || '';
      if (operation === 'create_retargeting_audience') {
        desc = `Create a retargeting audience from campaign ${platformCampaignId} for advertiser ${madhiveAdvertiserId} named "${audienceNameValue}"`;
      } else if (operation === 'create_website_retargeting_audience') {
        desc = `Create a website retargeting audience for advertiser ${madhiveAdvertiserId} with pixel name ${madhivePixelName}, segment name ${madhiveSegmentName}, audience name ${audienceNameValue}, retargeting window ${madhiveRetargetingWindow}`;
      } else if (operation === 'apply_audience') {
        desc = `apply audience ${audienceIdValue} to line item ${madhiveLineItemId}`;
      } else if (operation === 'create_publisher_group') {
        desc = 'create_publisher_group';
        configPayload = {
          operation: 'create_publisher_group',
          name: publisherGroupName,
          media_type: madhiveMediaType,
          product_id: madhiveProductId,
        };
        // CSV file path will be added after upload in the async submit handler
      } else if (operation === 'assign_publisher_group') {
        desc = `assign publisher group ${publisherGroupId} to line item ${madhiveLineItemId}`;
      } else if (operation === 'create_audience') {
        desc = `Create addressable segments for MadHive campaign ${platformCampaignId}`;
        configPayload = {
          operation: 'create_addressable_segments',
          audience_name: audienceNameValue.trim() || null,
          ...(launchConfigGcsPath && { launch_config_gcs_path: launchConfigGcsPath })
        };
      } else if (operation === 'update_campaign_name') {
        // Description wording mirrors the Frequence file-drop edit path
        // (frequenceFileScheduler parseEditEnvelope) so the MadHive edit
        // reasoning resolves the same operations deterministically.
        desc = `Rename the campaign to "${madhiveCampaignName.trim()}"`;
      } else if (operation === 'update_campaign_dates') {
        const parts = [];
        if (madhiveStartDate) parts.push(`set the start date to ${madhiveStartDate}`);
        if (madhiveEndDate) parts.push(`set the end date to ${madhiveEndDate}`);
        desc = `Update the campaign flight dates on all line items in this campaign: ${parts.join(' and ')}`;
      } else if (operation === 'update_campaign_geo') {
        const codes = madhivePostalCodes.split(/[\s,]+/).filter(Boolean).join(', ');
        if (madhiveGeoMode === 'add') {
          desc = `Add postal codes ${codes} to the geo targeting on all line items in this campaign`;
        } else if (madhiveGeoMode === 'remove') {
          desc = `Remove postal codes ${codes} from the geo targeting on all line items in this campaign`;
        } else {
          desc = `Replace the geo targeting on all line items in this campaign with postal codes: ${codes}`;
        }
      } else if (operation === 'update_campaign_fcap') {
        desc = `Set the daily frequency cap to ${madhiveFcapDaily} on all line items`;
      } else if (operation === 'update_campaign_daypart') {
        const startHour = parseInt(madhiveDaypartStartHour, 10);
        const endHour = parseInt(madhiveDaypartEndHour, 10);
        const hours = [];
        for (let h = startHour; h <= endHour; h++) hours.push(h);
        const excludeMap = {};
        madhiveDaypartDays.forEach(day => { excludeMap[day] = hours; });
        const mapJson = JSON.stringify(excludeMap);
        if (madhiveDaypartMode === 'add') {
          desc = `Add these dayparting exclusions on all line items in this campaign (hours listed are hours when ads will NOT run): ${mapJson}`;
        } else if (madhiveDaypartMode === 'remove') {
          desc = `Remove these dayparting exclusions on all line items in this campaign (re-enable these hours): ${mapJson}`;
        } else {
          desc = `Replace the dayparting exclusions on all line items in this campaign with exactly this excludeDayparting map (hours listed are hours when ads will NOT run): ${mapJson}`;
        }
      } else if (operation === 'update_campaign_budget') {
        desc = `Set the impression goal to ${madhiveImpressionGoal} on all line items`;
      } else if (operation === 'zero_domains_from_campaign_opt_template') {
        const domains = madhiveOptTemplateDomains.split(/[\n,]+/).map(d => d.trim()).filter(Boolean).join(', ');
        desc = `Zero out (set publisher cap to 0%) these publisher domains in the optimization template attached to this campaign's line items: ${domains}`;
      }
    }

    // TTD deterministic operations: set desc to operation name
    if (platform === 'ttd') {
      desc = operation; // e.g., "extend_end_date"
      configPayload = {};
      if (operation === 'extend_end_date') {
        configPayload.end_date = endDateValue;
      } else if (operation === 'update_campaign_budget') {
        try {
          configPayload = JSON.parse(configTextArea);
        } catch (e) {
          configPayload = { budget: configTextArea };
        }
      } else if (operation === 'update_conversion_reporting') {
        try {
          configPayload = JSON.parse(configTextArea);
        } catch (e) {
          configPayload = { config: configTextArea };
        }
      } else if (operation === 'update_adgroup_name') {
        configPayload.adgroup_id = adgroupIdValue;
        configPayload.new_name = budgetValue; // budgetValue reused for new name input
      } else if (operation === 'assign_audience') {
        // Use multi-select ad group IDs if available, otherwise fall back to text input
        configPayload.adgroup_ids = selectedAdgroupIds.length > 0
          ? selectedAdgroupIds
          : adgroupIdValue ? adgroupIdValue.split(',').map(s => s.trim()) : [];
        configPayload.audience_id = audienceIdValue;
      } else if (operation === 'assign_deals_to_adgroup') {
        configPayload.adgroup_ids = selectedAdgroupIds.length > 0
          ? selectedAdgroupIds
          : selectedTtdCampaignIds.length > 0
            ? selectedTtdCampaignIds
            : adgroupIdValue ? adgroupIdValue.split(',').map(s => s.trim()) : [];
        configPayload.deals = selectedDealIds.map(dealId => {
          const deal = pmpDeals.find(d => d.deal_id === dealId);
          return { SupplyVendorId: deal?.supply_vendor_id, SupplyVendorDealId: deal?.deal_id };
        });
      } else if (operation === 'create_audience') {
        try {
          configPayload = JSON.parse(configTextArea);
        } catch (e) {
          configPayload = { config: configTextArea };
        }
        configPayload.audience_name = audienceNameValue;
      } else if (operation === 'create_adgroup') {
        try {
          configPayload = JSON.parse(configTextArea);
        } catch (e) {
          configPayload = { config: configTextArea };
        }
      } else if (operation === 'refresh_audience') {
        // Pass GCS path so handler can download directly, bypassing LLM corruption.
        // The config_payload goes through metadata (not message text) via campaigns.js.
        if (launchConfigGcsPath) {
          configPayload = { launch_config_gcs_path: launchConfigGcsPath };
        } else {
          try {
            configPayload = { launch_config: JSON.parse(configTextArea) };
          } catch (e) {
            configPayload = { launch_config: configTextArea };
          }
        }
      }
      // Include selected campaign IDs (set last to survive JSON.parse overrides)
      if (allPlatformCampaignIds.length > 1) {
        if (isTtdMultiSelect && selectedTtdCampaignIds.length > 0) {
          configPayload.campaign_ids = selectedTtdCampaignIds;
        } else if (selectedTtdCampaignId) {
          configPayload.campaign_id = selectedTtdCampaignId;
        }
      }
    }

    // Add entity information if specific entities are selected
    if (showEntitySelection && selectedEntities.length > 0) {
      const entityType = availableEntities[0]?.type === 'adset' ? 'ad set' :
                        availableEntities[0]?.type === 'line_item' ? 'line item' :
                        availableEntities[0]?.type === 'creative' ? 'creative' :
                        'ad group';

      // For creatives (Facebook or MadHive), include creative ID in the description
      if (availableEntities[0]?.type === 'creative') {
        const selectedCreatives = availableEntities.filter(e => selectedEntities.includes(e.id));
        if (operation === 'creative_landing_page') {
          // For landing page update, format as expected: "change landing page to URL for creative ID"
          const creativeIds = selectedCreatives.map(c => c.id).join(', ');
          desc = `change landing page to ${landingPageValue} for creative ${creativeIds}`;
        } else if (operation === 'status' && (platform === 'facebook' || platform === 'fb')) {
          // For Facebook creative status, format as expected: "pause/activate creative ID"
          const creativeIds = selectedCreatives.map(c => c.id).join(', ');
          desc = `${statusValue === 'PAUSED' ? 'pause' : 'activate'} creative ${creativeIds}`;
        } else {
          // For MadHive or other platforms, include line_item_id
          const creativeDetails = selectedCreatives.map(c =>
            `creative_id: ${c.id}, line_item_id: ${c.line_item_id}`
          ).join('; ');
          desc = `${desc} for creatives: ${creativeDetails}`;
        }
      } else if (selectedEntities.length === availableEntities.length) {
        desc = `${desc} for all ${entityType}s`;
      } else {
        const selectedNames = availableEntities
          .filter(e => selectedEntities.includes(e.id))
          .map(e => e.name)
          .join(', ');
        desc = `${desc} for ${entityType}s: ${selectedNames}`;
      }
    }

    // Add custom description if provided
    if (description.trim()) {
      desc = `${desc}. ${description}`;
    }

    // MadHive: upload CSV file before submitting create_publisher_group
    if (platform === 'madhive' && operation === 'create_publisher_group' && publishersCsvFile) {
      try {
        setCsvUploading(true);
        const formData = new FormData();
        formData.append('files', publishersCsvFile);
        const uploadRes = await fetch(`/api/v2/campaigns/${campaign.id}/files`, {
          method: 'POST',
          body: formData,
        });
        const uploadData = await uploadRes.json();
        if (uploadData.files && uploadData.files.length > 0) {
          // Store GCS path in configPayload for the handler
          configPayload.publishers_csv_path = uploadData.files[0].file_path || uploadData.files[0].gcsPath;
        } else {
          alert('Failed to upload CSV file');
          setCsvUploading(false);
          return;
        }
        setCsvUploading(false);
      } catch (err) {
        console.error('CSV upload failed:', err);
        alert('Failed to upload CSV file: ' + err.message);
        setCsvUploading(false);
        return;
      }
    }

    onSubmit(desc, selectedEntities, configPayload);
  };

  const requiresBidAmount = (platform === 'facebook' || platform === 'fb') && operation === 'bid_strategy' &&
    (bidStrategy === 'LOWEST_COST_WITH_BID_CAP' || bidStrategy === 'COST_CAP');

  const isFormValid = () => {
    // Prompt-only platforms: the prompt IS the request, so a non-empty one is
    // the whole of the validation.
    if (isPromptOnly) {
      return description.trim() !== '';
    }

    // Check if entity selection is required and valid
    // Skip for TTD multi-select ops that use selectedAdgroupIds instead of selectedEntities
    const skipEntityCheck = platform === 'ttd' && ['assign_deals_to_adgroup', 'assign_audience'].includes(operation);
    if (showEntitySelection && selectedEntities.length === 0 && !skipEntityCheck) {
      return false;
    }

    if (operation === 'status') {
      return statusValue !== '';
    } else if (operation === 'delete') {
      // Campaign-level delete needs no extra input; the confirm dialog gates it.
      return true;
    } else if (operation === 'daily_budget' || operation === 'budget') {
      return budgetValue !== '' && parseFloat(budgetValue) > 0;
    } else if (operation === 'bid_strategy') {
      if (platform === 'reddit') {
        return bidAmount !== '' && parseFloat(bidAmount) > 0;
      } else if (platform === 'facebook' || platform === 'fb') {
        if (bidStrategy === '') return false;
        if (requiresBidAmount) {
          return bidAmount !== '' && parseFloat(bidAmount) > 0;
        }
        return true;
      }
    } else if (operation === 'campaign_spend_limit') {
      return spendLimitValue !== '' && parseFloat(spendLimitValue) > 0;
    } else if (operation === 'extend_end_date') {
      return endDateValue !== '';
    } else if (operation === 'creative_landing_page') {
      // Validate URL format (basic check)
      return landingPageValue !== '' && landingPageValue.startsWith('http');
    } else if (operation === 'geography_targeting') {
      // Validate at least one geography is entered
      return geographyValue.trim() !== '';
    } else if (operation === 'interest_targeting') {
      // Validate at least one interest is entered
      return interestValue.trim() !== '';
    } else if (operation === 'community_targeting') {
      // Validate at least one community/subreddit is entered
      return communityValue.trim() !== '';
    } else if (operation === 'keyword_targeting' || operation === 'pause_keywords') {
      // Validate at least one keyword is entered
      return keywordValue.trim() !== '';
    } else if (operation === 'location_targeting') {
      // Validate at least one location is entered
      return locationValue.trim() !== '';
    }

    // MadHive-specific validation
    if (platform === 'madhive') {
      if (operation === 'create_retargeting_audience') {
        return audienceNameValue.trim() !== '' && madhiveAdvertiserId.trim() !== '';
      } else if (operation === 'create_website_retargeting_audience') {
        return madhiveAdvertiserId.trim() !== '' && madhivePixelName.trim() !== '' &&
               madhiveSegmentName.trim() !== '' && audienceNameValue.trim() !== '';
      } else if (operation === 'apply_audience') {
        return audienceIdValue.trim() !== '' && madhiveLineItemId.trim() !== '';
      } else if (operation === 'create_publisher_group') {
        return publisherGroupName.trim() !== '' && madhiveMediaType !== '' && madhiveProductId.trim() !== '' && publishersCsvFile !== null;
      } else if (operation === 'assign_publisher_group') {
        return publisherGroupId.trim() !== '' && madhiveLineItemId.trim() !== '';
      } else if (operation === 'create_audience') {
        return audienceNameValue.trim() !== '' && configAutoLoaded;
      } else if (operation === 'update_campaign_name') {
        return madhiveCampaignName.trim().length >= 2;
      } else if (operation === 'update_campaign_dates') {
        if (madhiveStartDate === '' && madhiveEndDate === '') return false;
        if (madhiveStartDate && madhiveEndDate && madhiveStartDate >= madhiveEndDate) return false;
        return true;
      } else if (operation === 'update_campaign_geo') {
        return madhivePostalCodes.split(/[\s,]+/).filter(Boolean).length > 0;
      } else if (operation === 'update_campaign_fcap') {
        const fcap = parseInt(madhiveFcapDaily, 10);
        return madhiveFcapDaily !== '' && Number.isInteger(fcap) && fcap >= 1 && fcap <= 20;
      } else if (operation === 'update_campaign_daypart') {
        return madhiveDaypartDays.length > 0
          && parseInt(madhiveDaypartStartHour, 10) <= parseInt(madhiveDaypartEndHour, 10);
      } else if (operation === 'update_campaign_budget') {
        const goal = parseInt(madhiveImpressionGoal, 10);
        return madhiveImpressionGoal !== '' && Number.isInteger(goal) && goal > 0;
      } else if (operation === 'zero_domains_from_campaign_opt_template') {
        return madhiveOptTemplateDomains.split(/[\n,]+/).map(d => d.trim()).filter(Boolean).length > 0;
      }
    }

    // TTD-specific validation
    if (platform === 'ttd') {
      if (operation === 'extend_end_date') {
        return endDateValue !== '';
      } else if (operation === 'update_campaign_budget' || operation === 'update_conversion_reporting' || operation === 'create_adgroup') {
        if (configTextArea.trim() === '') return false;
        try { JSON.parse(configTextArea); return true; } catch (e) { return false; }
      } else if (operation === 'update_adgroup_name') {
        return adgroupIdValue.trim() !== '' && budgetValue.trim() !== '';
      } else if (operation === 'assign_audience') {
        return (selectedAdgroupIds.length > 0 || adgroupIdValue.trim() !== '') && audienceIdValue.trim() !== '';
      } else if (operation === 'assign_deals_to_adgroup') {
        return (selectedTtdCampaignIds.length > 0 || selectedAdgroupIds.length > 0 || adgroupIdValue.trim() !== '') && selectedDealIds.length > 0;
      } else if (operation === 'create_audience') {
        if (audienceNameValue.trim() === '') return false;
        if (configTextArea.trim() === '') return false;
        try { JSON.parse(configTextArea); return true; } catch (e) { return false; }
      } else if (operation === 'refresh_audience') {
        if (configTextArea.trim() === '') return false;
        try { JSON.parse(configTextArea); return true; } catch (e) { return false; }
      }
      return true;
    }

    return false;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content edit-campaign-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit {config.name} Campaign</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Operation Type */}
            {!isPromptOnly && (
              <div className="form-group">
                <label htmlFor="operation">Operation Type</label>
                <select
                  id="operation"
                  value={operation}
                  onChange={(e) => setOperation(e.target.value)}
                  className="form-control"
                >
                  {config.operations.map(op => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Level */}
            {!isPromptOnly && (
              <div className="form-group">
                <label htmlFor="level">Level</label>
                <select
                  id="level"
                  value={level}
                  onChange={(e) => setLevel(e.target.value)}
                  className="form-control"
                >
                  {currentLevels.map(lvl => (
                    <option key={lvl.value} value={lvl.value}>{lvl.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* TTD Campaign Selector — when multiple campaigns exist */}
            {platform === 'ttd' && allPlatformCampaignIds.length > 1 && (
              <div className="form-group">
                {isTtdMultiSelect ? (
                  <>
                    <label>
                      Campaigns
                      <span className="entity-count"> ({allPlatformCampaignIds.length} available)</span>
                    </label>
                    <div className="select-all-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={selectedTtdCampaignIds.length === allPlatformCampaignIds.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTtdCampaignIds(allPlatformCampaignIds.map(c => c.id));
                            } else {
                              setSelectedTtdCampaignIds([]);
                            }
                          }}
                        />
                        <span>Select All</span>
                      </label>
                    </div>
                    <div className="entity-list">
                      {allPlatformCampaignIds.map(c => (
                        <div key={c.id} className="entity-item">
                          <label>
                            <input
                              type="checkbox"
                              checked={selectedTtdCampaignIds.includes(c.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedTtdCampaignIds(prev => [...prev, c.id]);
                                } else {
                                  setSelectedTtdCampaignIds(prev => prev.filter(id => id !== c.id));
                                }
                              }}
                            />
                            <span className="entity-name">{c.name ? `${c.name} (${c.id})` : c.id}</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <label htmlFor="ttdCampaignId">Campaign</label>
                    <select
                      id="ttdCampaignId"
                      value={selectedTtdCampaignId}
                      onChange={(e) => setSelectedTtdCampaignId(e.target.value)}
                      className="form-control"
                    >
                      {allPlatformCampaignIds.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name ? `${c.name} (${c.id})` : c.id}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
            )}

            {/* Entity Selection (Ad Groups/Ad Sets/Line Items/Creatives) */}
            {showEntitySelection && (
              <div className="form-group entity-selection">
                <label>
                  Select {
                    availableEntities[0]?.type === 'adset' ? 'Ad Sets' :
                    availableEntities[0]?.type === 'line_item' ? 'Line Items' :
                    availableEntities[0]?.type === 'creative' ? 'Creatives' :
                    'Ad Groups'
                  } to Edit
                  <span className="entity-count"> ({availableEntities.length} available)</span>
                </label>

                <div className="select-all-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectAll(checked);
                        if (checked) {
                          setSelectedEntities(availableEntities.map(e => e.id));
                        } else {
                          setSelectedEntities([]);
                        }
                      }}
                    />
                    <span>Select All</span>
                  </label>
                </div>

                <div className="entity-list">
                  {availableEntities.map(entity => (
                    <div key={entity.id} className="entity-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={selectedEntities.includes(entity.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEntities([...selectedEntities, entity.id]);
                            } else {
                              setSelectedEntities(selectedEntities.filter(id => id !== entity.id));
                              setSelectAll(false);
                            }
                          }}
                        />
                        <span className="entity-name">{entity.name}</span>
                        <span className="entity-id">ID: {entity.id}</span>
                      </label>
                    </div>
                  ))}
                </div>

                {selectedEntities.length === 0 && (
                  <div className="warning-message">
                    ⚠️ Please select at least one {
                      availableEntities[0]?.type === 'adset' ? 'ad set' :
                      availableEntities[0]?.type === 'line_item' ? 'line item' :
                      availableEntities[0]?.type === 'creative' ? 'creative' :
                      'ad group'
                    } to edit
                  </div>
                )}
              </div>
            )}

            {/* Status Value */}
            {operation === 'status' && (
              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  value={statusValue}
                  onChange={(e) => setStatusValue(e.target.value)}
                  className="form-control"
                  required
                >
                  <option value="">Select status...</option>
                  {config.statusValues.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Delete Campaign warning */}
            {operation === 'delete' && (
              <div className="form-group">
                <div
                  style={{
                    padding: '12px 14px',
                    borderRadius: 8,
                    border: '1px solid #f1b0b7',
                    background: '#fdf0f1',
                    color: '#842029',
                    fontSize: 14,
                    lineHeight: 1.4,
                  }}
                >
                  <strong>⚠️ This permanently deletes the campaign on MadHive.</strong>
                  <div style={{ marginTop: 4 }}>
                    This action cannot be undone. You'll be asked to confirm before it runs.
                  </div>
                </div>
              </div>
            )}

            {/* Budget Value */}
            {(operation === 'daily_budget' || operation === 'budget') && (
              <div className="form-group">
                <label htmlFor="budget">
                  {operation === 'budget' && level === 'ad_group' ? 'CPC Bid' : 'Daily Budget'} ({accountCurrency})
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  id="budget"
                  value={budgetValue}
                  onChange={(e) => setBudgetValue(e.target.value)}
                  className="form-control"
                  placeholder={`Enter amount in ${accountCurrency}`}
                  required
                />
              </div>
            )}

            {/* Bid Strategy (Facebook only) */}
            {operation === 'bid_strategy' && (platform === 'facebook' || platform === 'fb') && (
              <>
                <div className="form-group">
                  <label htmlFor="bidStrategy">Bid Strategy</label>
                  <select
                    id="bidStrategy"
                    value={bidStrategy}
                    onChange={(e) => setBidStrategy(e.target.value)}
                    className="form-control"
                    required
                  >
                    <option value="">Select bid strategy...</option>
                    {config.bidStrategies.map(strategy => (
                      <option key={strategy.value} value={strategy.value}>{strategy.label}</option>
                    ))}
                  </select>
                </div>
                {requiresBidAmount && (
                  <div className="form-group">
                    <label htmlFor="bidAmount">Bid Amount ({accountCurrency})</label>
                    <input
                      type="number"
                      id="bidAmount"
                      value={bidAmount}
                      onChange={(e) => setBidAmount(e.target.value)}
                      className="form-control"
                      placeholder={`Enter bid amount in ${accountCurrency}`}
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                )}
              </>
            )}

            {/* Bid Value (Reddit only) */}
            {operation === 'bid_strategy' && platform === 'reddit' && (
              <div className="form-group">
                <label htmlFor="bidValue">Bid Value ({accountCurrency})</label>
                <input
                  type="number"
                  id="bidValue"
                  value={bidAmount}
                  onChange={(e) => setBidAmount(e.target.value)}
                  className="form-control"
                  placeholder={`Enter bid value in ${accountCurrency}`}
                  min="0"
                  step="0.01"
                  required
                />
              </div>
            )}

            {/* Campaign Spend Limit (Facebook, Reddit, Google Ads) */}
            {operation === 'campaign_spend_limit' && (platform === 'facebook' || platform === 'fb' || platform === 'reddit' || platform === 'google-ads-search' || platform === 'google_search') && (
              <div className="form-group">
                <label htmlFor="spendLimit">Campaign Spend Limit ({accountCurrency})</label>
                <input
                  type="number"
                  id="spendLimit"
                  value={spendLimitValue}
                  onChange={(e) => setSpendLimitValue(e.target.value)}
                  className="form-control"
                  placeholder={`Enter total campaign spend cap in ${accountCurrency}`}
                  min="0"
                  step="0.01"
                  required
                />
                <small className="form-text text-muted">
                  This sets a maximum total spend cap for the entire campaign.
                </small>
              </div>
            )}

            {/* End Date Update (Facebook and Reddit) */}
            {operation === 'extend_end_date' && (platform === 'facebook' || platform === 'fb' || platform === 'reddit') && (
              <div className="form-group">
                <label htmlFor="endDate">New End Date</label>
                <input
                  type="date"
                  id="endDate"
                  value={endDateValue}
                  onChange={(e) => setEndDateValue(e.target.value)}
                  className="form-control"
                  required
                />
                <small className="form-text text-muted">
                  {level === 'campaign'
                    ? (platform === 'facebook' || platform === 'fb')
                      ? 'Update the campaign end date. This will only update the end date of all ad sets in this campaign.'
                      : platform === 'reddit'
                        ? 'Update the campaign end date. This will only update the end date of all ad groups in this campaign.'
                        : 'Update the campaign end date to the selected date.'
                    : 'Update the end date for the selected ad sets.'}
                </small>
              </div>
            )}

            {/* Creative Landing Page (Facebook only) */}
            {operation === 'creative_landing_page' && (platform === 'facebook' || platform === 'fb') && (
              <div className="form-group">
                <label htmlFor="landingPage">Landing Page URL</label>
                <input
                  type="url"
                  id="landingPage"
                  value={landingPageValue}
                  onChange={(e) => setLandingPageValue(e.target.value)}
                  className="form-control"
                  placeholder="https://example.com/landing-page"
                  required
                />
                <small className="form-text text-muted">
                  Update the landing page URL for the selected creative(s).
                </small>
              </div>
            )}

            {/* Geography Targeting (Facebook) */}
            {operation === 'geography_targeting' && (platform === 'facebook' || platform === 'fb') && (
              <div className="form-group">
                <label htmlFor="geography">Geographies to Add</label>
                <input
                  type="text"
                  id="geography"
                  value={geographyValue}
                  onChange={(e) => setGeographyValue(e.target.value)}
                  className="form-control"
                  placeholder="e.g., 94102, 10001, 60601"
                  required
                />
                <small className="form-text text-muted">
                  Enter zip codes separated by commas. These will be added to the selected ad sets.
                </small>
              </div>
            )}

            {/* Geography Targeting (Reddit - zipcodes) */}
            {operation === 'geography_targeting' && platform === 'reddit' && (
              <div className="form-group">
                <label htmlFor="geography">Zipcodes to Add</label>
                <input
                  type="text"
                  id="geography"
                  value={geographyValue}
                  onChange={(e) => setGeographyValue(e.target.value)}
                  className="form-control"
                  placeholder="e.g., 94086, 94087, 10001"
                  required
                />
                <small className="form-text text-muted">
                  Enter zipcodes separated by commas. These will be added to the ad group geography targeting.
                </small>
              </div>
            )}

            {/* Interest Targeting (Facebook only) */}
            {operation === 'interest_targeting' && (platform === 'facebook' || platform === 'fb') && (
              <div className="form-group">
                <label htmlFor="interest">Interests to Add</label>
                <input
                  type="text"
                  id="interest"
                  value={interestValue}
                  onChange={(e) => setInterestValue(e.target.value)}
                  className="form-control"
                  placeholder="e.g., Digital Marketing, AI, Marketing Automation"
                  required
                />
                <small className="form-text text-muted">
                  Enter interest keywords separated by commas. These will be added to the selected ad sets.
                </small>
              </div>
            )}

            {/* Community Targeting (Reddit only) */}
            {operation === 'community_targeting' && platform === 'reddit' && (
              <div className="form-group">
                <label htmlFor="community">Communities/Subreddits to Add</label>
                <input
                  type="text"
                  id="community"
                  value={communityValue}
                  onChange={(e) => setCommunityValue(e.target.value)}
                  className="form-control"
                  placeholder="e.g., travel, technology, marketing"
                  required
                />
                <small className="form-text text-muted">
                  Enter subreddit names separated by commas (without r/). These will be added to the ad group community targeting.
                </small>
              </div>
            )}

            {/* Keyword Targeting (Reddit only) */}
            {operation === 'keyword_targeting' && platform === 'reddit' && (
              <div className="form-group">
                <label htmlFor="keyword">Keywords to Add</label>
                <input
                  type="text"
                  id="keyword"
                  value={keywordValue}
                  onChange={(e) => setKeywordValue(e.target.value)}
                  className="form-control"
                  placeholder="e.g., automation, AI tools, productivity"
                  required
                />
                <small className="form-text text-muted">
                  Enter keywords separated by commas. These will be added to the ad group keyword targeting.
                </small>
              </div>
            )}

            {/* Location Targeting (Google Ads only) */}
            {operation === 'location_targeting' && (platform === 'google-ads-search' || platform === 'google_search') && (
              <div className="form-group">
                <label htmlFor="location">Locations to Add</label>
                <input
                  type="text"
                  id="location"
                  value={locationValue}
                  onChange={(e) => setLocationValue(e.target.value)}
                  className="form-control"
                  placeholder="e.g., Chicago, New York, Los Angeles"
                  required
                />
                <small className="form-text text-muted">
                  Enter city or region names. These will be added to the campaign location targeting.
                </small>
              </div>
            )}

            {/* Keyword Targeting (Google Ads only) */}
            {operation === 'keyword_targeting' && (platform === 'google-ads-search' || platform === 'google_search') && (
              <div className="form-group">
                <label htmlFor="gadsKeyword">Keywords to Add</label>
                <input
                  type="text"
                  id="gadsKeyword"
                  value={keywordValue}
                  onChange={(e) => setKeywordValue(e.target.value)}
                  className="form-control"
                  placeholder="e.g., media operations, sidekick, digital marketing"
                  required
                />
                <small className="form-text text-muted">
                  Enter keywords separated by commas. These will be added to the selected ad group(s).
                </small>
              </div>
            )}

            {/* Pause Keywords (Google Ads only) */}
            {operation === 'pause_keywords' && (platform === 'google-ads-search' || platform === 'google_search') && (
              <div className="form-group">
                <label htmlFor="pauseKeyword">Keywords to Pause</label>
                <input
                  type="text"
                  id="pauseKeyword"
                  value={keywordValue}
                  onChange={(e) => setKeywordValue(e.target.value)}
                  className="form-control"
                  placeholder="e.g., buy shoes, running shoes"
                  required
                />
                <small className="form-text text-muted">
                  Enter existing keywords separated by commas. These keywords will be paused in the selected ad group(s).
                </small>
              </div>
            )}

            {/* TTD: Extend End Date */}
            {operation === 'extend_end_date' && platform === 'ttd' && (
              <div className="form-group">
                <label htmlFor="endDate">New End Date</label>
                <input
                  type="date"
                  id="endDate"
                  value={endDateValue}
                  onChange={(e) => setEndDateValue(e.target.value)}
                  className="form-control"
                  required
                />
                <small className="form-text text-muted">
                  Extend the campaign and flight end dates to the selected date.
                </small>
              </div>
            )}

            {/* TTD: Update Ad Group Name */}
            {operation === 'update_adgroup_name' && platform === 'ttd' && (
              <>
                <div className="form-group">
                  <label htmlFor="adgroupId">Ad Group ID</label>
                  {availableEntities.length > 0 ? (
                    <select
                      id="adgroupId"
                      value={adgroupIdValue}
                      onChange={(e) => setAdgroupIdValue(e.target.value)}
                      className="form-control"
                      required
                    >
                      <option value="">Select ad group...</option>
                      {availableEntities.map(e => (
                        <option key={e.id} value={e.id}>{e.name ? `${e.name} (${e.id})` : e.id}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      id="adgroupId"
                      value={adgroupIdValue}
                      onChange={(e) => setAdgroupIdValue(e.target.value)}
                      className="form-control"
                      placeholder="Enter TTD Ad Group ID"
                      required
                    />
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="newName">New Ad Group Name</label>
                  <input
                    type="text"
                    id="newName"
                    value={budgetValue}
                    onChange={(e) => setBudgetValue(e.target.value)}
                    className="form-control"
                    placeholder="Enter new ad group name"
                    required
                  />
                </div>
              </>
            )}

            {/* TTD: Assign Audience */}
            {operation === 'assign_audience' && platform === 'ttd' && (
              <>
                <div className="form-group">
                  <label>
                    Ad Groups
                    {availableEntities.length > 0 && (
                      <span className="entity-count"> ({availableEntities.length} available)</span>
                    )}
                  </label>
                  {availableEntities.length > 0 ? (
                    <>
                      <div className="select-all-checkbox">
                        <label>
                          <input
                            type="checkbox"
                            checked={selectedAdgroupIds.length === availableEntities.length && availableEntities.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedAdgroupIds(availableEntities.map(e => e.id));
                              } else {
                                setSelectedAdgroupIds([]);
                              }
                            }}
                          />
                          <span>Select All</span>
                        </label>
                      </div>
                      <div className="entity-list">
                        {availableEntities.map(entity => (
                          <div key={entity.id} className="entity-item">
                            <label>
                              <input
                                type="checkbox"
                                checked={selectedAdgroupIds.includes(entity.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedAdgroupIds(prev => [...prev, entity.id]);
                                  } else {
                                    setSelectedAdgroupIds(prev => prev.filter(id => id !== entity.id));
                                  }
                                }}
                              />
                              <span className="entity-name">{entity.name ? `${entity.name} (${entity.id})` : entity.id}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <input
                      type="text"
                      id="adgroupIdAssign"
                      value={adgroupIdValue}
                      onChange={(e) => setAdgroupIdValue(e.target.value)}
                      className="form-control"
                      placeholder="Enter comma-separated TTD Ad Group IDs"
                      required
                    />
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="audienceId">Audience ID</label>
                  <input
                    type="text"
                    id="audienceId"
                    value={audienceIdValue}
                    onChange={(e) => setAudienceIdValue(e.target.value)}
                    className="form-control"
                    placeholder="Enter TTD Audience ID"
                    required
                  />
                </div>
              </>
            )}

            {/* TTD: Assign PMP Deals - deals dropdown */}
            {operation === 'assign_deals_to_adgroup' && platform === 'ttd' && (
              <div className="form-group">
                <label>PMP Deals</label>
                {pmpDealsLoading && (
                  <div style={{ padding: '8px 0', color: '#6c757d', fontSize: '13px' }}>
                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '6px' }} />
                    Loading PMP deals...
                  </div>
                )}
                {!pmpDealsLoading && pmpDeals.length > 0 && (
                  <>
                    <div style={{ marginBottom: '6px', fontSize: '13px', color: '#6c757d' }}>
                      {selectedDealIds.length} of {pmpDeals.length} deals selected
                    </div>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', padding: '4px' }}>
                      {pmpDeals.map(deal => (
                        <label key={deal.deal_id} style={{ display: 'flex', alignItems: 'center', padding: '4px 8px', cursor: 'pointer', fontSize: '13px' }}>
                          <input
                            type="checkbox"
                            checked={selectedDealIds.includes(deal.deal_id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedDealIds(prev => [...prev, deal.deal_id]);
                              } else {
                                setSelectedDealIds(prev => prev.filter(id => id !== deal.deal_id));
                              }
                            }}
                            style={{ marginRight: '8px' }}
                          />
                          <span>{deal.deal_name}</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}
                {!pmpDealsLoading && pmpDeals.length === 0 && (
                  <div style={{ padding: '8px 0', color: '#dc3545', fontSize: '13px' }}>
                    No PMP deals available.
                  </div>
                )}
              </div>
            )}

            {/* TTD: Refresh Audience - auto-loaded launcher config */}
            {operation === 'refresh_audience' && platform === 'ttd' && (
              <div className="form-group">
                <label>Launcher Config</label>
                {configLoading && (
                  <div style={{ padding: '8px 0', color: '#6c757d', fontSize: '13px' }}>
                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '6px' }} />
                    Loading launcher config...
                  </div>
                )}
                {configAutoLoaded && (
                  <div style={{ padding: '8px 0', color: '#28a745', fontSize: '13px' }}>
                    <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '6px' }} />
                    Auto-loaded from launcher config
                  </div>
                )}
                {!configLoading && !configAutoLoaded && (
                  <div style={{ padding: '8px 0', color: '#dc3545', fontSize: '13px' }}>
                    No launcher config found — this campaign may not have a completed launch.
                  </div>
                )}
              </div>
            )}

            {/* TTD: Create Audience - audience name input */}
            {operation === 'create_audience' && platform === 'ttd' && (
              <div className="form-group">
                <label htmlFor="audienceName">Audience Name</label>
                <input
                  type="text"
                  id="audienceName"
                  value={audienceNameValue}
                  onChange={(e) => setAudienceNameValue(e.target.value)}
                  className="form-control"
                  placeholder="Enter name for the new audience"
                  required
                />
              </div>
            )}

            {/* MadHive: Create Retargeting Audience */}
            {operation === 'create_retargeting_audience' && platform === 'madhive' && (
              <>
                <div className="form-group">
                  <label htmlFor="madhiveAudienceName">Audience Name</label>
                  <input
                    type="text"
                    id="madhiveAudienceName"
                    value={audienceNameValue}
                    onChange={(e) => setAudienceNameValue(e.target.value)}
                    className="form-control"
                    placeholder='e.g. "Q2 retargeting test"'
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="madhiveAdvertiserId">Advertiser ID</label>
                  <input
                    type="text"
                    id="madhiveAdvertiserId"
                    value={madhiveAdvertiserId}
                    onChange={(e) => setMadhiveAdvertiserId(e.target.value)}
                    className="form-control"
                    placeholder="Enter MadHive Advertiser ID"
                    required
                  />
                </div>
              </>
            )}

            {/* MadHive: Create Website Retargeting Audience */}
            {operation === 'create_website_retargeting_audience' && platform === 'madhive' && (
              <>
                <div className="form-group">
                  <label htmlFor="madhiveAdvIdWebRtg">Advertiser ID</label>
                  <input
                    type="text"
                    id="madhiveAdvIdWebRtg"
                    value={madhiveAdvertiserId}
                    onChange={(e) => setMadhiveAdvertiserId(e.target.value)}
                    className="form-control"
                    placeholder="Enter MadHive Advertiser ID"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="madhivePixelName">Pixel Name</label>
                  <input
                    type="text"
                    id="madhivePixelName"
                    value={madhivePixelName}
                    onChange={(e) => setMadhivePixelName(e.target.value)}
                    className="form-control"
                    placeholder='e.g. "Homepage Pixel"'
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="madhiveSegmentName">Segment Name</label>
                  <input
                    type="text"
                    id="madhiveSegmentName"
                    value={madhiveSegmentName}
                    onChange={(e) => setMadhiveSegmentName(e.target.value)}
                    className="form-control"
                    placeholder='e.g. "Homepage Visitors Segment"'
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="madhiveAudienceNameWebRtg">Audience Name</label>
                  <input
                    type="text"
                    id="madhiveAudienceNameWebRtg"
                    value={audienceNameValue}
                    onChange={(e) => setAudienceNameValue(e.target.value)}
                    className="form-control"
                    placeholder='e.g. "Homepage Visitors Audience"'
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="madhiveRetargetingWindow">Retargeting Window</label>
                  <select
                    id="madhiveRetargetingWindow"
                    value={madhiveRetargetingWindow}
                    onChange={(e) => setMadhiveRetargetingWindow(e.target.value)}
                    className="form-control"
                  >
                    <option value="7 DAY">7 Days</option>
                    <option value="14 DAY">14 Days</option>
                    <option value="30 DAY">30 Days</option>
                    <option value="60 DAY">60 Days</option>
                    <option value="90 DAY">90 Days</option>
                    <option value="180 DAY">180 Days</option>
                  </select>
                </div>
              </>
            )}

            {/* MadHive: Apply Audience to Line Item */}
            {operation === 'apply_audience' && platform === 'madhive' && (
              <>
                <div className="form-group">
                  <label htmlFor="madhiveAudienceId">Audience ID</label>
                  <input
                    type="text"
                    id="madhiveAudienceId"
                    value={audienceIdValue}
                    onChange={(e) => setAudienceIdValue(e.target.value)}
                    className="form-control"
                    placeholder="Enter MadHive Audience ID"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="madhiveLineItemId">Line Item ID</label>
                  <input
                    type="text"
                    id="madhiveLineItemId"
                    value={madhiveLineItemId}
                    onChange={(e) => setMadhiveLineItemId(e.target.value)}
                    className="form-control"
                    placeholder="Enter MadHive Line Item ID"
                    required
                  />
                </div>
              </>
            )}

            {/* MadHive: Create Publisher Group */}
            {operation === 'create_publisher_group' && platform === 'madhive' && (
              <>
                <div className="form-group">
                  <label htmlFor="publisherGroupName">Publisher Group Name</label>
                  <input
                    type="text"
                    id="publisherGroupName"
                    value={publisherGroupName}
                    onChange={(e) => setPublisherGroupName(e.target.value)}
                    className="form-control"
                    placeholder='e.g. "Display List 2"'
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="madhiveMediaType">Media Type</label>
                  <select
                    id="madhiveMediaType"
                    value={madhiveMediaType}
                    onChange={(e) => setMadhiveMediaType(e.target.value)}
                    className="form-control"
                  >
                    <option value="DISPLAY">DISPLAY</option>
                    <option value="VIDEO">VIDEO</option>
                    <option value="AUDIO">AUDIO</option>
                    <option value="NONE">NONE</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="madhiveProductId">Product ID</label>
                  <input
                    type="text"
                    id="madhiveProductId"
                    value={madhiveProductId}
                    onChange={(e) => setMadhiveProductId(e.target.value)}
                    className="form-control"
                    placeholder="Enter MadHive Product ID"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="publishersCsv">Publishers CSV File</label>
                  <input
                    type="file"
                    id="publishersCsv"
                    accept=".csv"
                    onChange={(e) => setPublishersCsvFile(e.target.files[0] || null)}
                    className="form-control"
                    required
                  />
                  {publishersCsvFile && (
                    <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                      Selected: {publishersCsvFile.name}
                    </small>
                  )}
                  {csvUploading && (
                    <small style={{ color: '#007bff', marginTop: '4px', display: 'block' }}>
                      Uploading CSV...
                    </small>
                  )}
                </div>
              </>
            )}

            {/* MadHive: Assign Publisher Group to Line Item */}
            {operation === 'assign_publisher_group' && platform === 'madhive' && (
              <>
                <div className="form-group">
                  <label htmlFor="publisherGroupId">Publisher Group ID</label>
                  <input
                    type="text"
                    id="publisherGroupId"
                    value={publisherGroupId}
                    onChange={(e) => setPublisherGroupId(e.target.value)}
                    className="form-control"
                    placeholder="Enter MadHive Publisher Group ID"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="madhiveLineItemIdAssign">Line Item ID</label>
                  <input
                    type="text"
                    id="madhiveLineItemIdAssign"
                    value={madhiveLineItemId}
                    onChange={(e) => setMadhiveLineItemId(e.target.value)}
                    className="form-control"
                    placeholder="Enter MadHive Line Item ID"
                    required
                  />
                </div>
              </>
            )}

            {/* MadHive: Create Audience - audience name + auto-loaded launcher config */}
            {operation === 'create_audience' && platform === 'madhive' && (
              <>
                <div className="form-group">
                  <label htmlFor="madhiveAudienceNameCreate">Audience Name</label>
                  <input
                    type="text"
                    id="madhiveAudienceNameCreate"
                    value={audienceNameValue}
                    onChange={(e) => setAudienceNameValue(e.target.value)}
                    className="form-control"
                    placeholder="Enter name for the new audience"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Launcher Config</label>
                  {configLoading && (
                    <div style={{ padding: '8px 0', color: '#6c757d', fontSize: '13px' }}>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '6px' }} />
                      Loading launcher config...
                    </div>
                  )}
                  {configAutoLoaded && (
                    <div style={{ padding: '8px 0', color: '#28a745', fontSize: '13px' }}>
                      <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '6px' }} />
                      Auto-loaded launcher config with addressable segments
                    </div>
                  )}
                  {!configLoading && !configAutoLoaded && (
                    <div style={{ padding: '8px 0', color: '#dc3545', fontSize: '13px' }}>
                      No launcher config found — this campaign may not have a completed launch.
                    </div>
                  )}
                </div>
              </>
            )}

            {/* MadHive: Update Campaign Name */}
            {operation === 'update_campaign_name' && platform === 'madhive' && (
              <div className="form-group">
                <label htmlFor="madhiveCampaignName">New Campaign Name</label>
                <input
                  type="text"
                  id="madhiveCampaignName"
                  value={madhiveCampaignName}
                  onChange={(e) => setMadhiveCampaignName(e.target.value)}
                  className="form-control"
                  placeholder="Enter the new campaign name (min 2 characters)"
                  required
                />
              </div>
            )}

            {/* MadHive: Update Flight Dates — applied to all line items */}
            {operation === 'update_campaign_dates' && platform === 'madhive' && (
              <>
                <div className="form-group">
                  <label htmlFor="madhiveStartDate">Start Date</label>
                  <input
                    type="date"
                    id="madhiveStartDate"
                    value={madhiveStartDate}
                    onChange={(e) => setMadhiveStartDate(e.target.value)}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="madhiveEndDate">End Date</label>
                  <input
                    type="date"
                    id="madhiveEndDate"
                    value={madhiveEndDate}
                    onChange={(e) => setMadhiveEndDate(e.target.value)}
                    className="form-control"
                  />
                  <small className="form-text text-muted">
                    Provide at least one date. Dates apply to all line items in the campaign.
                  </small>
                </div>
              </>
            )}

            {/* MadHive: Update Geo Targeting (postal codes, all line items) */}
            {operation === 'update_campaign_geo' && platform === 'madhive' && (
              <>
                <div className="form-group">
                  <label htmlFor="madhivePostalCodes">Postal Codes</label>
                  <input
                    type="text"
                    id="madhivePostalCodes"
                    value={madhivePostalCodes}
                    onChange={(e) => setMadhivePostalCodes(e.target.value)}
                    className="form-control"
                    placeholder="e.g. 92844, 90630, 10001"
                    required
                  />
                  <small className="form-text text-muted">
                    Comma or space separated ZIP/postal codes. Applies to all line items.
                  </small>
                </div>
                <div className="form-group">
                  <label htmlFor="madhiveGeoMode">Mode</label>
                  <select
                    id="madhiveGeoMode"
                    value={madhiveGeoMode}
                    onChange={(e) => setMadhiveGeoMode(e.target.value)}
                    className="form-control"
                  >
                    <option value="replace">Replace existing targeting</option>
                    <option value="add">Add to existing targeting</option>
                    <option value="remove">Remove from existing targeting</option>
                  </select>
                </div>
              </>
            )}

            {/* MadHive: Update Daily Frequency Cap (all line items) */}
            {operation === 'update_campaign_fcap' && platform === 'madhive' && (
              <div className="form-group">
                <label htmlFor="madhiveFcapDaily">Daily Frequency Cap</label>
                <input
                  type="number"
                  id="madhiveFcapDaily"
                  value={madhiveFcapDaily}
                  onChange={(e) => setMadhiveFcapDaily(e.target.value)}
                  className="form-control"
                  placeholder="1-20"
                  min="1"
                  max="20"
                  step="1"
                  required
                />
                <small className="form-text text-muted">
                  Maximum impressions per day per user (1-20). Applies to all line items.
                </small>
              </div>
            )}

            {/* MadHive: Update Dayparting Exclusions (all line items) */}
            {operation === 'update_campaign_daypart' && platform === 'madhive' && (
              <>
                <div className="form-group">
                  <label>Days to Exclude</label>
                  <div className="entity-list">
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                      <div key={day} className="entity-item">
                        <label>
                          <input
                            type="checkbox"
                            checked={madhiveDaypartDays.includes(day)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setMadhiveDaypartDays(prev => [...prev, day]);
                              } else {
                                setMadhiveDaypartDays(prev => prev.filter(d => d !== day));
                              }
                            }}
                          />
                          <span className="entity-name">{day.charAt(0).toUpperCase() + day.slice(1)}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="madhiveDaypartStartHour">Excluded Hours (ads will NOT run)</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <select
                      id="madhiveDaypartStartHour"
                      value={madhiveDaypartStartHour}
                      onChange={(e) => setMadhiveDaypartStartHour(e.target.value)}
                      className="form-control"
                    >
                      {Array.from({ length: 24 }, (_, h) => (
                        <option key={h} value={String(h)}>{h}:00</option>
                      ))}
                    </select>
                    <span>to</span>
                    <select
                      id="madhiveDaypartEndHour"
                      value={madhiveDaypartEndHour}
                      onChange={(e) => setMadhiveDaypartEndHour(e.target.value)}
                      className="form-control"
                    >
                      {Array.from({ length: 24 }, (_, h) => (
                        <option key={h} value={String(h)}>{h}:59</option>
                      ))}
                    </select>
                  </div>
                  <small className="form-text text-muted">
                    Hour range (inclusive) blocked on the selected days. Default 0:00-23:59 blocks the entire day.
                  </small>
                </div>
                <div className="form-group">
                  <label htmlFor="madhiveDaypartMode">Mode</label>
                  <select
                    id="madhiveDaypartMode"
                    value={madhiveDaypartMode}
                    onChange={(e) => setMadhiveDaypartMode(e.target.value)}
                    className="form-control"
                  >
                    <option value="replace">Replace existing exclusions</option>
                    <option value="add">Add to existing exclusions</option>
                    <option value="remove">Remove (re-enable these hours)</option>
                  </select>
                </div>
              </>
            )}

            {/* MadHive: Update Impression Goal (all line items) */}
            {operation === 'update_campaign_budget' && platform === 'madhive' && (
              <div className="form-group">
                <label htmlFor="madhiveImpressionGoal">Impression Goal</label>
                <input
                  type="number"
                  id="madhiveImpressionGoal"
                  value={madhiveImpressionGoal}
                  onChange={(e) => setMadhiveImpressionGoal(e.target.value)}
                  className="form-control"
                  placeholder="e.g. 500000"
                  min="1"
                  step="1"
                  required
                />
                <small className="form-text text-muted">
                  Total impression goal. Applies to all line items in the campaign.
                </small>
              </div>
            )}

            {/* MadHive: Zero Out Domains in Optimization Template */}
            {operation === 'zero_domains_from_campaign_opt_template' && platform === 'madhive' && (
              <div className="form-group">
                <label htmlFor="madhiveOptTemplateDomains">Publisher Domains</label>
                <textarea
                  id="madhiveOptTemplateDomains"
                  value={madhiveOptTemplateDomains}
                  onChange={(e) => setMadhiveOptTemplateDomains(e.target.value)}
                  className="form-control"
                  placeholder={'e.g. Dexerto.com, Keepingitheel.com\nOne per line or comma separated'}
                  rows="4"
                  required
                />
                <small className="form-text text-muted">
                  Comma or newline separated publisher domains/names. Sets the publisher cap to 0% in the
                  optimization template (Supply Guardrail) attached to this campaign's line items, so the
                  publishers stay in the template but stop delivering.
                </small>
              </div>
            )}

            {/* General: File Upload + Text Area for JSON config */}
            {needsConfigInput(operation, platform) && (
              <div className="form-group">
                <label>JSON Configuration</label>
                <div
                  className={`config-file-drop-zone ${configDragActive ? 'active' : ''}`}
                  onDragEnter={handleConfigDrag}
                  onDragLeave={handleConfigDrag}
                  onDragOver={handleConfigDrag}
                  onDrop={handleConfigDrop}
                  onClick={() => configFileInputRef.current?.click()}
                >
                  <FontAwesomeIcon icon={faUpload} size="lg" />
                  <p>Drop JSON file here or click to browse</p>
                </div>
                <input
                  ref={configFileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleConfigFileSelect}
                  style={{ display: 'none' }}
                />
                <textarea
                  value={configTextArea}
                  onChange={(e) => setConfigTextArea(e.target.value)}
                  className="form-control config-textarea"
                  placeholder="Or paste JSON config here..."
                  rows="6"
                />
                {configTextArea && (() => {
                  try { JSON.parse(configTextArea); return null; }
                  catch (e) { return <small className="form-text" style={{ color: '#dc3545' }}>Invalid JSON: {e.message}</small>; }
                })()}
              </div>
            )}

            {/* Prompt (the whole request on prompt-only platforms) */}
            <div className="form-group">
              <label htmlFor="description">
                {isPromptOnly ? 'What should change?' : 'Additional Instructions (Optional)'}
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="form-control"
                placeholder={isPromptOnly
                  ? 'e.g. "change geo for all PTG lines to NYC", "decrease bids by 15% on all RTG lines", or "extend the campaign through April 30"'
                  : 'Add any additional instructions or context...'}
                rows={isPromptOnly ? '4' : '3'}
              />
              {isPromptOnly && (
                <small className="form-text">
                  Applies to the line items and insertion orders in this campaign that the prompt
                  names — say which ("all RTG lines", "all line items", "every IO") and what to
                  change. Flight dates and budgets live on the insertion order, so an unqualified
                  "extend the campaign" or "raise the budget" changes every IO. Anything the DV360
                  editor cannot express comes back as not yet supported, with nothing changed.
                </small>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!isFormValid() || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  {' '}Submitting...
                </>
              ) : (
                'Submit Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCampaignModal;

